// Focus-flow UI strings — the Typeform-shaped create / refine / implement / chat
// surface (apps/web/src/focus/*). Sandbox-phase pill labels, the pipeline step
// names, every headline / help / button / placeholder the wizard renders, plus
// the per-field `detail` slots (the "details" disclosure copy) that complete the
// field.<name>.* catalog the rest of which lives in fields.ts.

import type { MsgSection } from "./types.js";

export const focus: MsgSection = {
  // Reused by ReadinessCard widgets (chatRender slice) — preserved from the
  // foundation seed so those references resolve.
  "focus.typeAnswer": { en: "Type your answer…", de: "Antwort eingeben…" },
  "focus.addCriterion": { en: "Add a criterion…", de: "Kriterium hinzufügen…" },
  // ─── Sandbox-phase pill / headline labels (SANDBOX_LABEL) ──────────────
  // The keys mirror the SandboxPhase enum; only the display text is here.
  // Each one completes a sentence that starts with "Loom": the refine boot
  // headline is "Loom is {phase}…" / "Loom {phase}…", so a German label is an
  // active verb phrase. "Rechner wird bereitgestellt" rendered as "Loom Rechner
  // wird bereitgestellt…" — the German main line for the whole runner wait.
  "focus.sandbox.idle": { en: "waking up", de: "startet" },
  "focus.sandbox.triggering": { en: "waking up", de: "startet" },
  "focus.sandbox.waiting-for-runner": {
    en: "getting a machine ready",
    de: "stellt einen Rechner bereit",
  },
  "focus.sandbox.booting": {
    en: "reading your codebase",
    de: "liest deinen Code",
  },
  "focus.sandbox.ready": { en: "live", de: "ist bereit" },
  "focus.sandbox.degraded": { en: "reconnecting", de: "verbindet neu" },
  "focus.sandbox.suspended": { en: "paused", de: "pausiert" },
  // Not a sentence: the refine page has its own screen for a stopped
  // workspace, so this only ever labels Focus Chat's pill.
  "focus.sandbox.terminated": { en: "stopped", de: "gestoppt" },
  "focus.sandbox.gettingSetUp": { en: "getting set up", de: "richtet sich ein" },

  // ─── Shared status-pill labels ─────────────────────────────────────────
  "focus.status.sessionExpired": { en: "session expired", de: "Sitzung abgelaufen" },
  "focus.status.accessDenied": { en: "access denied", de: "Zugriff verweigert" },
  "focus.status.thinking": { en: "thinking…", de: "denkt nach…" },
  "focus.status.live": { en: "live", de: "live" },
  "focus.status.stopped": { en: "stopped", de: "gestoppt" },
  "focus.status.applied": { en: "applied", de: "übernommen" },
  "focus.status.applyFailed": { en: "apply failed", de: "Übernehmen fehlgeschlagen" },
  "focus.status.applying": { en: "applying…", de: "wird übernommen…" },
  "focus.status.starting": { en: "starting", de: "startet" },
  "focus.status.budgetStopped": { en: "out of budget", de: "Budget aufgebraucht" },
  // The page's OWN socket is down: a socket this mount had, or a first connect
  // that has not opened. It outranks "thinking…" and "live" — a page that cannot
  // hear Loom cannot say either.
  "focus.status.reconnecting": { en: "reconnecting…", de: "verbindet neu…" },
  "focus.status.connecting": { en: "connecting…", de: "verbindet…" },
  // The human-idle hard kill. It leaves the flow on screen (resumable in place),
  // so the pill is the only chrome that can say the session behind it is gone —
  // live it went on reading "thinking…".
  "focus.status.sessionClosed": { en: "session closed", de: "Sitzung geschlossen" },

  // ─── Session-level refusals ────────────────────────────────────────────
  // The prose under the status pill. The pill says WHAT happened in two words;
  // these say what to do about it. Loom cannot grant access to itself — the way
  // out is a person, not a button.
  //
  // The non-403 leg. `session.error` is an HttpError message — "503 Service
  // Unavailable: {"error":"membership_check_unavailable"}" — which is a
  // debugging string, not copy. The status still reaches the console and the
  // network tab; what the reader gets is the consequence and the way out.
  "focus.session.startFailedHelp": {
    en: "Loom couldn't start a session for this project just now. This is usually temporary — try again in a moment.",
    de: "Loom konnte gerade keine Sitzung für dieses Projekt starten. Das ist meist vorübergehend — versuche es gleich noch einmal.",
  },
  // One refusal, three causes — and the sentence has to match the cause, or it
  // sends the reader on the wrong errand. Live, a user who already held
  // Developer on GitLab and only lacked Loom's own grant was told to go ask
  // GitLab for that role. The reason comes from `denialReasonOf`
  // (focus/useProjectAccess.ts); each line below answers exactly one of them.
  //
  // The GitLab leg. Names the permission by the word GitLab uses on its own
  // members screen so the reader can go ask for it by name.
  "focus.session.accessDenied.gitlab": {
    en: "You don't have Developer access on this project in GitLab, so Loom can't start a session for it. Ask a project admin to grant it.",
    de: "Du hast auf diesem GitLab-Projekt keinen Developer-Zugriff, daher kann Loom dafür keine Sitzung starten. Bitte eine Administratorin oder einen Administrator des Projekts in GitLab, dir den Zugriff zu geben.",
  },
  // The Loom leg. GitLab is already in order, so the line says so — otherwise
  // the reader spends a day chasing a role they have. "Freigeben" is the word
  // the admin surface itself uses for this act.
  "focus.session.accessDenied.loom": {
    en: "This project hasn't been opened up for you in Loom yet, so Loom can't start a session for it. Your GitLab access is fine — ask a Loom administrator to approve you for this project.",
    de: "Dieses Projekt ist in Loom noch nicht für dich freigegeben, daher kann Loom dafür keine Sitzung starten. Dein Zugriff in GitLab ist in Ordnung — bitte eine Loom-Administratorin oder einen Loom-Administrator, dir dieses Projekt freizugeben.",
  },
  // Both legs missing, and the leg a bare 403 from `POST /sessions` cannot
  // name. One way out, one person to ask — two chores in one sentence would
  // read as a checklist nobody can start.
  "focus.session.accessDenied.either": {
    en: "You don't have access to this project yet, so Loom can't start a session for it. Ask an administrator to set you up.",
    de: "Du hast für dieses Projekt noch keinen Zugriff, daher kann Loom dafür keine Sitzung starten. Bitte eine Administratorin oder einen Administrator, dir den Zugriff einzurichten.",
  },

  // ─── Frame chrome (ui.tsx) ─────────────────────────────────────────────
  "focus.button.ok": { en: "OK", de: "OK" },
  "focus.enterHint.press": { en: "press", de: "drücke" },
  "focus.help.detailsToggle": { en: "details", de: "Details" },
  "focus.messageBar.placeholderDefault": {
    en: "Or tell Loom in your own words…",
    de: "Oder sag es Loom in eigenen Worten…",
  },
  "focus.messageBar.ariaLabel": { en: "Message Loom", de: "Nachricht an Loom" },

  // ─── FocusChat ─────────────────────────────────────────────────────────
  "focus.chat.headline": { en: "Chat with Loom", de: "Mit Loom chatten" },
  "focus.chat.help": {
    en: "Ask anything about this project — no specific task needed. Loom knows the codebase and can help you think things through.",
    de: "Frag alles zum Projekt — du brauchst keine konkrete Aufgabe. Loom kennt den Code und denkt mit dir mit.",
  },
  "focus.chat.placeholder": { en: "Message Loom…", de: "Nachricht an Loom…" },
  "focus.chat.thinking": { en: "Loom is thinking…", de: "Loom denkt nach…" },
  // Loom's proposal, pinned above the composer while the user refines an issue
  // in the chat: the `draft` of each open story field and list, each one click
  // from being the user's own answer (ChatProposalBlock).
  "focus.chat.proposal.title": {
    en: "Loom’s proposal for #{iid}",
    de: "Vorschlag von Loom für #{iid}",
  },
  "focus.chat.proposal.help": {
    en: "Take a line as it is, or write your own in the message box.",
    de: "Übernimm eine Zeile, wie sie ist, oder schreib deine eigene ins Nachrichtenfeld.",
  },
  // A row whose name line ("So that:") is in the composer: the user is writing
  // that field there, so the row offers no second way to answer it.
  "focus.chat.proposal.writingBelow": {
    en: "Being written in the message box",
    de: "Wird im Nachrichtenfeld geschrieben",
  },
  // A paused or stopped chat does not wake for a click: the answers wait for
  // the user's next Send, which is what wakes it.
  "focus.chat.proposal.paused": {
    en: "Loom is paused — your answers go out with your next message.",
    de: "Loom pausiert — deine Antworten gehen mit deiner nächsten Nachricht raus.",
  },
  // A chat never sends while Loom works (a message then would kill the turn),
  // so unlike the issue page there is no one-minute backstop to promise.
  "focus.chat.proposal.holdBusy": {
    en: "Loom is still working — your answers go out when it’s done.",
    de: "Loom arbeitet noch — deine Antworten gehen raus, sobald es fertig ist.",
  },

  // ─── Implement pipeline step labels (ImplementPipeline.tsx) ────────────
  "focus.pipeline.aria": {
    en: "Implementation progress",
    de: "Umsetzungsfortschritt",
  },
  "focus.pipeline.boot": { en: "Prepare workspace", de: "Umgebung vorbereiten" },
  "focus.pipeline.understand": {
    en: "Understand the issue",
    de: "Issue verstehen",
  },
  "focus.pipeline.implement": { en: "Implement", de: "Umsetzen" },
  "focus.pipeline.verify": {
    en: "Verify (quality checks)",
    de: "Prüfen (Qualitätsprüfungen)",
  },
  "focus.pipeline.mr": { en: "Open merge request", de: "Merge Request öffnen" },
  "focus.pipeline.pr": { en: "Open pull request", de: "Pull Request öffnen" },
  "focus.pipeline.deploy": {
    en: "Deploy review app",
    de: "Review-App deployen",
  },

  // ─── ImplementFlow ─────────────────────────────────────────────────────
  "focus.implement.sandboxLive": { en: "workspace ready", de: "Umgebung bereit" },
  "focus.implement.reconnecting": { en: "reconnecting", de: "verbindet neu" },
  "focus.implement.sandboxStopped": { en: "workspace stopped", de: "Umgebung gestoppt" },
  "focus.implement.starting": { en: "starting", de: "startet" },
  "focus.implement.booting": { en: "starting", de: "startet" },
  "focus.implement.kicker": { en: "Implement", de: "Umsetzen" },
  "focus.implement.titleWithName": { en: "#{iid} · {title}", de: "#{iid} · {title}" },
  "focus.implement.titleFallback": { en: "Issue #{iid}", de: "Issue #{iid}" },
  "focus.implement.viewMr": { en: "View merge request", de: "Merge Request ansehen" },
  "focus.implement.viewPr": { en: "View pull request", de: "Pull Request ansehen" },
  "focus.implement.openEnv": { en: "Open {name}", de: "{name} öffnen" },
  "focus.implement.openIssue": { en: "Open issue #{iid}", de: "Issue #{iid} öffnen" },
  "focus.implement.startError.headline": {
    en: "Couldn't start the build",
    de: "Build liess sich nicht starten",
  },
  // Shown BEFORE a new sandbox session is dispatched, when this issue already
  // has an open merge request — the one moment left to stop a second,
  // disconnected build from starting on top of real, already-open work.
  // Two headlines, because the attribution is not free: `open_mr` opens the MR
  // with the SESSION USER's own OAuth token, so a Loom-built MR and a
  // colleague's hand-written one carry the same `author`. The only marker that
  // survives to the browser is the `loom-coding/` source branch
  // (`isLoomAuthoredMr`, ImplementFlow.tsx). Anything else — a teammate's MR
  // whose body says `Closes #<iid>`, which is ordinary for an issue a human
  // started — gets the neutral pair, so the screen never tells the user Loom
  // built something it never touched. The help line under them is
  // author-agnostic and shared.
  "focus.implement.openMr.headline": {
    en: "Loom already built this",
    de: "Loom hat das bereits umgesetzt",
  },
  "focus.implement.openMr.headlineOther": {
    en: "This issue already has an open merge request",
    de: "Zu diesem Issue gibt es bereits einen offenen Merge Request",
  },
  "focus.implement.openMr.help": {
    en: "{title} already has an open merge request — “{mrTitle}”. Building it again starts a second, separate run that could conflict with that one.",
    de: "Zu {title} gibt es bereits einen offenen Merge Request — «{mrTitle}». Nochmals bauen startet einen zweiten, unabhängigen Lauf, der mit diesem kollidieren kann.",
  },
  "focus.implement.openMr.buildAnyway": {
    en: "Build again anyway",
    de: "Trotzdem nochmals bauen",
  },
  // "Build AGAIN" presumes a prior Loom build, so it pairs with the attributed
  // headline only; the neutral side says nothing about what came before.
  "focus.implement.openMr.buildAnywayOther": {
    en: "Build it anyway",
    de: "Trotzdem bauen",
  },
  "focus.implement.dor.help": {
    en: "This issue isn't ready to build yet — it's missing {missing}. You can refine it first, or have Loom proceed anyway.",
    de: "Dieses Issue ist noch nicht baubereit — es fehlt: {missing}. Du kannst es zuerst verfeinern oder Loom einfach weitermachen lassen.",
  },
  "focus.implement.dor.missingFallback": {
    en: "some readiness details",
    de: "ein paar Readiness-Angaben",
  },
  "focus.implement.dor.refineFirst": { en: "Refine first", de: "Zuerst verfeinern" },
  "focus.implement.dor.proceedAnyway": {
    en: "Proceed anyway",
    de: "Trotzdem fortfahren",
  },
  "focus.implement.epicHalt.help": {
    en: "This issue is a tracking epic — its work is carved up across the sub-issues below. Pick one and Loom builds it, or have Loom proceed on the epic anyway.",
    de: "Dieses Issue ist ein Tracking-Epic — seine Arbeit ist auf die Sub-Issues unten verteilt. Wähle eines aus, dann setzt Loom es um — oder lass Loom trotzdem am Epic weitermachen.",
  },
  "focus.implement.epicHalt.helpNoChildren": {
    en: "This issue is a tracking epic, but it has no sub-issues yet. Splitting it into buildable pieces happens in Refine — or have Loom proceed on the epic anyway.",
    de: "Dieses Issue ist ein Tracking-Epic, hat aber noch keine Sub-Issues. Aufgeteilt wird es beim Verfeinern — oder lass Loom trotzdem am Epic weitermachen.",
  },
  // The chip BUILDS the sub-issue (it used to leave Focus for the dashboard
  // issue editor, which the screen's own copy never promised).
  "focus.implement.epicHalt.buildChild": {
    en: "Build sub-issue #{iid}",
    de: "Sub-Issue #{iid} umsetzen",
  },
  // An epic with no children: the remedy is a SPLIT, not the DoR gate's
  // "refine first" — that label is for an issue missing readiness fields.
  "focus.implement.epicHalt.splitFirst": {
    en: "Split it into sub-issues first",
    de: "Zuerst in Sub-Issues aufteilen",
  },
  "focus.implement.epicHalt.proceedAnyway": {
    en: "Build the epic anyway",
    de: "Trotzdem am Epic bauen",
  },
  "focus.implement.done.reviewLive": { en: "Review app is live", de: "Review-App ist live" },
  "focus.implement.done.allDone": { en: "All done", de: "Alles erledigt" },
  "focus.implement.done.helpWithReview": {
    en: "Loom implemented {title}, opened a merge request, and deployed a review app.",
    de: "Loom hat {title} umgesetzt, einen Merge Request geöffnet und eine Review-App deployt.",
  },
  "focus.implement.done.help": {
    en: "Loom implemented {title} and opened a merge request.{detail}",
    de: "Loom hat {title} umgesetzt und einen Merge Request geöffnet.{detail}",
  },
  "focus.implement.done.helpPr": {
    en: "Loom implemented {title} and opened a pull request.",
    de: "Loom hat {title} umgesetzt und einen Pull Request geöffnet.",
  },
  "focus.implement.failed.headline": {
    en: "Couldn't finish automatically",
    de: "Konnte nicht automatisch fertig werden",
  },
  "focus.implement.failed.defaultDetail": {
    en: "The run stopped before a merge request was opened.",
    de: "Der Lauf stoppte, bevor ein Merge Request entstand.",
  },
  "focus.implement.failed.defaultDetailPr": {
    en: "The run stopped before a pull request was opened.",
    de: "Der Lauf stoppte, bevor ein Pull Request entstand.",
  },
  "focus.implement.failed.help": {
    en: "You can open the issue to take over, or try again.",
    de: "Du kannst das Issue öffnen und selbst übernehmen oder es nochmals versuchen.",
  },
  "focus.implement.deploy.help": {
    en: "The merge request is open. Loom is building and deploying the review app — this is a long-running step that can take several minutes. You can leave this page; it keeps running and the link appears here when it's ready.",
    de: "Der Merge Request ist offen. Loom baut und deployt jetzt die Review-App — das dauert schon mal ein paar Minuten. Du kannst die Seite ruhig verlassen; es läuft weiter und der Link taucht hier auf, sobald alles bereit ist.",
  },
  "focus.implement.build.help": {
    en: "Loom is building this on its own — reading the issue, writing the change, verifying it, and opening a merge request. This can take a while; you can leave this page and it keeps running.",
    de: "Loom macht das selbstständig — liest das Issue, schreibt die Änderung, prüft sie und öffnet einen Merge Request. Das kann eine Weile dauern; du kannst die Seite verlassen, es läuft weiter.",
  },
  "focus.implement.build.helpPr": {
    en: "Loom is building this on its own — reading the issue, writing the change, verifying it, and opening a pull request. This can take a while; you can leave this page and it keeps running.",
    de: "Loom macht das selbstständig — liest das Issue, schreibt die Änderung, prüft sie und öffnet einen Pull Request. Das kann eine Weile dauern; du kannst die Seite verlassen, es läuft weiter.",
  },

  // ─── FocusPage — project step ──────────────────────────────────────────
  "focus.project.kicker": { en: "Welcome", de: "Willkommen" },
  "focus.project.headline": {
    en: "Which project are we working in?",
    de: "In welchem Projekt arbeiten wir?",
  },
  "focus.project.searchAria": { en: "Search projects", de: "Projekte suchen" },
  "focus.project.searchPlaceholder": { en: "Search projects…", de: "Projekte suchen…" },
  "focus.project.loading": { en: "Loading projects…", de: "Projekte werden geladen…" },
  "focus.project.loadError": {
    en: "Couldn't load projects: {error}",
    de: "Projekte liessen sich nicht laden: {error}",
  },
  "focus.project.noneFound": { en: "No projects found.", de: "Keine Projekte gefunden." },
  "focus.project.noMatch": {
    en: "No projects match “{query}”.",
    de: "Kein Projekt passt zu «{query}».",
  },

  // ─── The account's GitHub connection (components/GitHubConnection.tsx) ──
  // A row on the account page: connecting GitHub is what lists the user's
  // GitHub projects in the Focus picker. ---
  "focus.github.connect": { en: "Connect GitHub", de: "GitHub verbinden" },
  "focus.github.reconnect": {
    en: "Reconnect GitHub",
    de: "GitHub neu verbinden",
  },
  "focus.github.connection.notConnected": {
    en: "GitHub isn't connected.",
    de: "GitHub ist nicht verbunden.",
  },
  "focus.github.connection.connected": {
    en: "GitHub is connected.",
    de: "GitHub ist verbunden.",
  },
  "focus.github.connection.connectedAs": {
    en: "GitHub is connected as {login}.",
    de: "GitHub ist mit {login} verbunden.",
  },
  // The row exists and Loom can no longer renew it. Reconnect sits beside it.
  "focus.github.connection.lapsed": {
    en: "Your GitHub connection has run out, so Loom can't reach your projects there.",
    de: "Deine GitHub-Verbindung ist abgelaufen, darum erreicht Loom deine Projekte dort nicht.",
  },
  "focus.github.connection.disconnect": { en: "Disconnect", de: "Trennen" },
  // The row's Disconnect trigger, named for what it ends: the page shows two.
  "focus.github.connection.disconnectLabel": {
    en: "Disconnect GitHub",
    de: "GitHub trennen",
  },
  "focus.github.connection.disconnecting": {
    en: "Disconnecting…",
    de: "Wird getrennt…",
  },
  "focus.github.connection.disconnected": {
    en: "Loom no longer uses your GitHub account.",
    de: "Loom nutzt dein GitHub-Konto nicht mehr.",
  },
  // Only when the server could not withdraw Loom's access at GitHub. The menu
  // names stay English: GitHub's own settings are.
  "focus.github.connection.githubStillLists": {
    en: "GitHub may still list Loom under Settings → Applications → Authorized OAuth Apps. Choose Revoke there if GitHub should forget it too.",
    de: "In GitHub steht Loom vielleicht noch unter Settings → Applications → Authorized OAuth Apps. Wähl dort Revoke, wenn auch GitHub es vergessen soll.",
  },
  "focus.github.connection.disconnectFailed": {
    en: "Loom couldn't disconnect GitHub. Try again.",
    de: "Loom konnte GitHub nicht trennen. Versuch es noch einmal.",
  },
  "focus.github.connection.confirm.title": {
    en: "Disconnect GitHub?",
    de: "GitHub trennen?",
  },
  "focus.github.connection.confirm.body": {
    en: "Your GitHub projects leave your project list. If Loom is working in one right now, it may not be able to save its changes there.",
    de: "Deine GitHub-Projekte verschwinden aus deiner Projektliste. Arbeitet Loom gerade in einem davon, kann es seine Änderungen dort vielleicht nicht mehr speichern.",
  },
  // Coming back from GitHub: the callback's four machine words.
  "focus.github.result.connected": {
    en: "Done. Your GitHub projects are now in your project list.",
    de: "Fertig. Deine GitHub-Projekte stehen jetzt in deiner Projektliste.",
  },
  "focus.github.result.accessDenied": {
    en: "You stopped the GitHub connection. Nothing was changed.",
    de: "Du hast die GitHub-Verbindung abgebrochen. Es wurde nichts geändert.",
  },
  "focus.github.result.oauthError": {
    en: "GitHub turned the connection down. You can try again.",
    de: "GitHub hat die Verbindung abgelehnt. Du kannst es noch einmal versuchen.",
  },
  "focus.github.result.exchangeFailed": {
    en: "Loom couldn't finish the connection with GitHub. Try again — this is usually temporary.",
    de: "Loom konnte die Verbindung mit GitHub nicht abschliessen. Versuch es noch einmal — das ist meist vorübergehend.",
  },

  // ─── FocusPage — issue search step ─────────────────────────────────────
  "focus.search.refineKicker": { en: "Refine", de: "Verfeinern" },
  "focus.search.implementKicker": { en: "Implement", de: "Umsetzen" },
  "focus.search.implementHeadline": {
    en: "Which issue should Loom build?",
    de: "Welches Issue soll Loom umsetzen?",
  },
  "focus.search.refineHeadline": {
    en: "Which issue do you want to refine?",
    de: "Welches Issue möchtest du verfeinern?",
  },
  // The list is there before you type, so the copy no longer instructs a search
  // as the only way in. It does NOT claim an ordering: the issue feed carries
  // whatever order GitLab returned, and promising "most recent" would be a claim
  // this surface cannot keep.
  "focus.search.help": {
    en: "Pick one from the list, or search by title or number.",
    de: "Wähle eines aus der Liste oder suche nach Titel oder Nummer.",
  },
  "focus.search.aria": {
    en: "Search issues by title or number",
    de: "Issues nach Titel oder Nummer suchen",
  },
  "focus.search.placeholder": {
    en: "Search a request by title or #number…",
    de: "Anfrage nach Titel oder #Nummer suchen…",
  },
  "focus.search.loading": { en: "Loading issues…", de: "Issues werden geladen…" },
  "focus.search.noMatch": { en: "No matching requests.", de: "Keine passende Anfrage." },
  "focus.search.empty": {
    en: "No open requests in this project yet.",
    de: "In diesem Projekt gibt es noch keine offenen Anfragen.",
  },
  // Second line under an issue in the pickers — see `issueMeta`.
  "focus.search.meta.ready": { en: "Ready", de: "Bereit" },

  // ─── FocusPage — intro choices ─────────────────────────────────────────
  "focus.intro.headline": {
    en: "What would you like to do?",
    de: "Was möchtest du tun?",
  },
  "focus.intro.create.title": { en: "Create a new issue", de: "Neues Issue erstellen" },
  "focus.intro.refine.title": { en: "Refine an issue", de: "Ein Issue verfeinern" },
  "focus.intro.implement.title": { en: "Implement an issue", de: "Ein Issue umsetzen" },
  "focus.intro.chat.title": { en: "Chat with Loom", de: "Mit Loom chatten" },

  // ─── "Vorschläge aus Mails" (`focus/mail/MailProposals.tsx`) ────────────
  // The user's own open mail proposals for this project, under the choices.
  // Accept / Decline and the status chip reuse the meetings keys.
  "focus.mail.title": { en: "Suggestions from emails", de: "Vorschläge aus Mails" },
  "focus.mail.untitled": { en: "Untitled suggestion", de: "Vorschlag ohne Titel" },
  "focus.mail.open": { en: "Open", de: "Öffnen" },
  "focus.mail.accepted": { en: "#{iid} accepted.", de: "#{iid} übernommen." },
  "focus.mail.declined": { en: "#{iid} declined.", de: "#{iid} verworfen." },
  "focus.mail.error.changed": {
    en: "This suggestion changed in the meantime. The list shows the new state.",
    de: "Dieser Vorschlag hat sich inzwischen geändert. Die Liste zeigt den neuen Stand.",
  },
  "focus.mail.error.publicProject": {
    en: "This project is public. A suggestion from an email can't be accepted there.",
    de: "Dieses Projekt ist öffentlich. Ein Vorschlag aus einer Mail lässt sich dort nicht übernehmen.",
  },
  "focus.mail.error.gone": {
    en: "This suggestion no longer exists.",
    de: "Diesen Vorschlag gibt es nicht mehr.",
  },
  "focus.mail.error.forbidden": {
    en: "You have no access to this project.",
    de: "Du hast keinen Zugriff auf dieses Projekt.",
  },
  "focus.mail.error.generic": {
    en: "That didn't work. Try again.",
    de: "Das hat nicht geklappt. Versuch es nochmals.",
  },

  // ─── FocusPage — create steps ──────────────────────────────────────────
  "focus.create.kicker": { en: "New request", de: "Neue Anfrage" },
  "focus.create.location.num": { en: "1 of 2", de: "1 von 2" },
  "focus.create.task.num": { en: "2 of 2", de: "2 von 2" },
  "focus.create.location.headline": {
    en: "Where in the codebase will this live?",
    de: "Wo im Code gehört das hin?",
  },
  "focus.create.location.help": {
    en: "An area, module, or path — it helps Loom get its bearings faster. Not sure? Skip it.",
    de: "Ein Bereich, ein Modul oder ein Pfad — so findet sich Loom schneller zurecht. Unsicher? Lass es einfach weg.",
  },
  "focus.create.location.aria": { en: "Codebase location", de: "Ort im Code" },
  "focus.create.location.placeholder": {
    en: "e.g. apps/web/src/dashboard, the OAuth flow, billing…",
    de: "z. B. apps/web/src/dashboard, der OAuth-Flow, Billing…",
  },
  "focus.create.location.skip": { en: "Skip", de: "Überspringen" },
  "focus.create.location.next": { en: "Next", de: "Weiter" },
  // What the create screen says while the server works, keyed on the SSE
  // phase. The server's own `message` is developer English ("Checking required
  // slots") and never reaches the user — see `CREATE_PROGRESS_KEY`.
  "focus.create.progress.linking": {
    en: "Looking for related issues…",
    de: "Verwandte Issues werden gesucht…",
  },
  "focus.create.progress.checking": {
    en: "Checking what's still missing…",
    de: "Es wird geprüft, was noch fehlt…",
  },
  "focus.create.progress.writing": {
    en: "Writing the issue…",
    de: "Das Issue wird angelegt…",
  },
  // ─── Projekt-Karte panel (clickable location map) ──────────────────────
  "focus.create.location.map.crumbRoot": {
    en: "Project map",
    de: "Projekt-Karte",
  },
  "focus.create.location.map.back": { en: "Back", de: "Zurück" },
  // NOT "describe it in chat": it opens a text box on this same step, and a
  // promise of chat that lands on a form is the kind of small lie that makes a
  // user distrust the rest of the screen.
  "focus.create.location.map.skip": {
    en: "I'm not sure — let me describe it instead",
    de: "Ich bin nicht sicher — ich beschreibe es lieber",
  },
  // Step 2 echoes the location step's answer. Without it the create flow asked
  // for a location, then never showed it again — and Back re-opened the map at
  // the root, so the only copy of the answer was in the user's memory.
  "focus.create.task.chosenLocation": {
    en: "Location: {path}",
    de: "Ort: {path}",
  },
  "focus.create.task.changeLocation": {
    en: "Change",
    de: "Ändern",
  },
  "focus.create.location.map.areas": {
    en: "{count} areas ›",
    de: "{count} Bereiche ›",
  },
  "focus.create.location.map.choose": { en: "select", de: "auswählen" },
  "focus.create.location.map.confirm": {
    en: "That's it — continue",
    de: "Das passt — weiter",
  },
  "focus.create.location.map.loading": {
    en: "Loading the project map…",
    de: "Projekt-Karte wird geladen…",
  },
  "focus.create.location.map.advisory": {
    en: "Starting point: {path}",
    de: "Ausgangspunkt: {path}",
  },
  "focus.create.task.headline": {
    en: "What needs to happen?",
    de: "Was soll passieren?",
  },
  "focus.create.task.help": {
    en: "Describe it in your own words — rough is fine. Loom sharpens it with you next. ⌘/Ctrl + Enter to continue.",
    de: "Beschreib es in eigenen Worten — grob reicht. Loom schärft es danach mit dir. ⌘/Strg + Enter zum Weitermachen.",
  },
  "focus.create.task.aria": { en: "What needs to happen", de: "Was passieren soll" },
  "focus.create.task.placeholder": {
    en: "e.g. Finance managers need to export a VAT summary as a PDF…",
    de: "z. B. Finanzmanager wollen eine USt-Zusammenfassung als PDF exportieren…",
  },
  "focus.create.task.submit": { en: "Create & refine", de: "Erstellen & verfeinern" },
  "focus.create.creating.headline": {
    en: "Filing your request…",
    de: "Deine Anfrage wird angelegt…",
  },
  // NO `focus.create.creating.help`. The create screen's help line is keyed on
  // the SSE phase (`CREATE_PROGRESS_KEY` → `focus.create.progress.*`), which is
  // strictly more informative than one static sentence; a second key saying
  // "creating the issue" only invites the next reader to render both.

  // ─── RefineFlow ────────────────────────────────────────────────────────
  "focus.refine.issueKicker": { en: "Issue #{iid}", de: "Issue #{iid}" },
  // Resume frames — shown when a refine URL is opened cold (reload / deep-link)
  // and we re-fetch the issue title before mounting the chat.
  "focus.refine.resume.loading": {
    en: "Reopening this issue…",
    de: "Issue wird wieder geöffnet…",
  },
  "focus.refine.resume.errorHeadline": {
    en: "Couldn’t reopen this issue",
    de: "Issue konnte nicht wieder geöffnet werden",
  },
  "focus.refine.resume.back": { en: "Back", de: "Zurück" },
  // The parked-flow notice. One session serves a whole PROJECT (see the header
  // of `dashboard/useProjectSession.ts`), so opening a second issue leaves the
  // first one exactly where it was — its card and its verified answers are
  // persisted per (session, issue). The switch is the architecture; the silence
  // was the defect.
  //
  // Not "parked": the user parked nothing, they opened something else. Not
  // "still open" either — that reads as the GitLab open/closed state, which is
  // not what this means. The reassurance is scoped to what is actually true:
  // the card's greens are saved. The agent's own conversation is NOT (a flow
  // boundary nulls `claudeSessionId`), so "nothing is lost" would be a promise
  // this surface cannot keep.
  // No COUNT of what is waiting there: the number that would be true is the one
  // that issue’s own step queue computes on landing, and predicting it from here
  // is a second copy of that logic that drifts. A bar that promises one question
  // and then shows three is worse in two languages than one that never counts.
  "focus.refine.parked.note": {
    en: "You’re still mid-way through #{iid}. Your answers there are saved.",
    de: "Du bist noch mitten in #{iid}. Deine Antworten dort sind gespeichert.",
  },
  // The same fact WITHOUT the reassurance, for a flow whose only trace is a
  // retired pre-card question. There is no card, so there may be no verified
  // answers to have saved — the switch may have landed on that issue’s very
  // first turn. Say the part that is true and stop.
  "focus.refine.parked.noteAsk": {
    en: "You’re still mid-way through #{iid}.",
    de: "Du bist noch mitten in #{iid}.",
  },
  // Offered only when that issue still has a card to come back TO. A flow whose
  // only trace is a retired ask has nothing to land on — no card, no pinned
  // question, and its own evidence withholds the seed — so it gets the sentence
  // without the button rather than a door onto an empty screen.
  "focus.refine.parked.resume": { en: "Back to #{iid}", de: "Zurück zu #{iid}" },
  // Skip affordance for an OPTIONAL field (e.g. milestone) — decline it instead
  // of being forced to pick a value.
  "focus.refine.skipOptional": {
    en: "Skip — leave this unset",
    de: "Überspringen — leer lassen",
  },
  // A typed question Loom raised on its own (not tied to one issue field), so
  // "leave this unset" would name a field that doesn't exist.
  "focus.refine.ask.skip": {
    en: "Skip this question",
    de: "Diese Frage überspringen",
  },
  // A CONCERN — Loom flagging a problem with something already settled, not
  // asking for something missing. The kicker names the shape so the user doesn't
  // read it as being asked the same thing twice; it stays neutral rather than
  // alarming, because most concerns are worth a shrug.
  "focus.refine.concern.kicker": {
    en: "Loom spotted something",
    de: "Loom ist etwas aufgefallen",
  },
  // Skipping a concern is a DECISION — "I've heard it, it stays as it is" — and
  // it is the only way to retract one, so it must not read as ducking a question
  // the user never got. It sits beside "Change it" with EQUAL weight and under
  // "What it says now", so it says exactly what it keeps: on #239 "Skip — leave
  // it as it is" was the only yes-shaped button and kept "so that yes".
  "focus.refine.concern.skip": {
    en: "Keep it as it is",
    de: "So lassen",
  },
  // The value a concern is ABOUT, shown above Keep / Change. Never "Loom's
  // draft": it is the field's current value, often the user's own words.
  "focus.refine.concern.current": { en: "What it says now", de: "Aktueller Stand" },
  // Opens an empty box for the concern reply. Never pre-filled, never an offer.
  "focus.refine.concern.change": { en: "Change it", de: "Ändern" },
  // The same, for a concern bound to no field — there is no value to change.
  "focus.refine.concern.reply": { en: "Reply", de: "Antworten" },
  // One-shot "take back my last answer" affordance, shown only while that answer
  // is still held locally (not yet sent to Loom).
  "focus.refine.undo.button": {
    en: "Undo — go back",
    de: "Rückgängig — zurück",
  },
  // Its accessible name carries the field it takes back.
  "focus.refine.undo.aria": {
    en: "Undo — go back ({label})",
    de: "Rückgängig — zurück ({label})",
  },
  "focus.refine.fatal.headline": { en: "Loom had to stop", de: "Loom musste abbrechen" },
  "focus.refine.fatal.startOver": { en: "Start over", de: "Neu starten" },
  // Every refine escape screen's way out, to the project picker.
  "focus.refine.fatal.goToProjects": { en: "Go to projects", de: "Zu den Projekten" },
  "focus.refine.bootFailed.headline": {
    en: "Loom couldn't start a session",
    de: "Loom konnte keine Sitzung starten",
  },
  "focus.refine.bootFailed.retry": { en: "Try again", de: "Erneut versuchen" },
  "focus.refine.bootFailed.retrying": { en: "Starting…", de: "Wird gestartet…" },
  // Terminal state when the sandbox can't reach Loom's tools. Says nothing
  // about MCP or bearers — the user needs the consequence and the way out.
  "focus.refine.tools.headline": {
    en: "Loom lost access to your project",
    de: "Loom hat den Zugriff auf dein Projekt verloren",
  },
  "focus.refine.tools.help": {
    en: "This session can't continue. Start over to pick it up again — your issue is unchanged.",
    de: "Diese Sitzung kann nicht fortgesetzt werden. Beginne neu, um weiterzumachen — dein Issue bleibt unverändert.",
  },
  "focus.refine.tools.helpApplyable": {
    en: "The card is already complete, so you can still apply it. Anything beyond that needs a fresh start.",
    de: "Die Karte ist bereits vollständig, du kannst sie also noch übernehmen. Alles Weitere braucht einen Neustart.",
  },
  // ─── The wait line (focus/WaitLine.tsx) ─────────────────────────────────
  // Everything here states a SERVER fact: the socket is down, the workspace is
  // in this phase, the agent is busy and calling this tool. None of it is a
  // guess from silence, and none of it asks the user to do anything.
  "focus.wait.offline": {
    en: "Connection to Loom lost — reconnecting…",
    de: "Verbindung zu Loom unterbrochen — verbinde neu…",
  },
  // The page's FIRST connect has not opened in 12 s. The page's own fact, not
  // the server's: it cannot reach Loom, and it keeps trying on its own.
  "focus.wait.unreachable": {
    en: "Can't reach Loom yet — still trying…",
    de: "Loom ist noch nicht erreichbar — neuer Versuch läuft…",
  },
  "focus.wait.soFar": { en: "{minutes} min so far", de: "bisher {minutes} Min." },
  // Waiting for a GitLab runner for three minutes or more. Measured on dev
  // (2026-09-24 → 26, n=103): median 18 s, p90 171 s, longest 763 s. A dead
  // pipeline is not this: the server's pipeline watch turns it into the
  // stopped screen, which is why "you don't need to do anything" is true here.
  "focus.wait.runnerLong": {
    en: "Loom's workspace hasn't started yet. That usually takes under a minute, but when many jobs are queued it can take several. Loom carries on by itself once it starts — you don't need to do anything.",
    de: "Looms Arbeitsbereich ist noch nicht gestartet. Meist dauert das unter einer Minute, wenn viele Jobs anstehen aber auch mehrere. Loom macht von selbst weiter, sobald er läuft — du musst nichts tun.",
  },
  "focus.wait.reconnecting": {
    en: "Loom lost the line to its workspace and is reconnecting. If that fails, this page will say so and offer a restart.",
    de: "Loom hat die Verbindung zu seinem Arbeitsbereich verloren und verbindet neu. Klappt das nicht, zeigt dir diese Seite das und bietet einen Neustart an.",
  },
  "focus.wait.working": { en: "Loom is working on it", de: "Loom arbeitet daran" },
  "focus.wait.workingFor": {
    en: "Loom is working on it · {minutes} min",
    de: "Loom arbeitet daran · {minutes} Min.",
  },
  // Three minutes busy: ~3% of turns (pre-card p95 159 s; 29 of 1189 turns
  // ran past 180 s). True, and it asks nothing — no restart, and no "you may
  // leave": leaving the page detaches the session and would stop the turn.
  "focus.wait.long": {
    en: "This one is taking longer than usual. Loom is still on it.",
    de: "Das dauert länger als sonst. Loom ist noch dran.",
  },
  "focus.wait.activity.code": { en: "Reading your code", de: "Liest deinen Code" },
  "focus.wait.activity.issue": { en: "Reading the issue", de: "Liest das Issue" },
  "focus.wait.activity.card": {
    en: "Putting the card together",
    de: "Stellt die Karte zusammen",
  },
  "focus.wait.activity.size": { en: "Estimating the size", de: "Schätzt den Umfang" },
  "focus.wait.activity.design": { en: "Reading the design", de: "Liest das Design" },
  "focus.wait.activity.milestones": {
    en: "Looking at the milestones",
    de: "Schaut sich die Meilensteine an",
  },
  // The workspace is up, the server says the agent is idle, and nothing is
  // open: no question, no card, no held answer — for 20 s. That is the one
  // state in which "nothing more is on its way" is a fact.
  "focus.refine.stopped.headline": {
    en: "Loom isn't working on this right now",
    de: "Loom arbeitet gerade nicht daran",
  },
  "focus.refine.stopped.help": {
    en: "Its last step ended without a question or a card, so nothing more is on its way. Start again and Loom reads the issue and your code once more — it may ask you again about something you already answered. Your issue hasn't been changed.",
    de: "Sein letzter Schritt endete ohne Frage und ohne Karte — es kommt also nichts mehr. Beginnst du neu, liest Loom das Issue und deinen Code noch einmal und fragt dich dabei vielleicht erneut, was du schon beantwortet hast. Dein Issue wurde nicht verändert.",
  },
  "focus.refine.stopped.silence": {
    en: "Its last step went silent for five minutes, so its workspace stopped it.",
    de: "Sein letzter Schritt war fünf Minuten lang still, darum hat sein Arbeitsbereich ihn abgebrochen.",
  },
  "focus.refine.stopped.startAgain": { en: "Start again", de: "Neu beginnen" },
  // The same server fact with a card on screen (`card_stopped`): the workspace
  // is up, the server says idle, nothing is waiting on the user — for 20 s —
  // and the card is not finished. The way on keeps the conversation and the
  // card: one same-flow turn, no restart.
  "focus.refine.cardStopped.help": {
    en: "Its last step ended before the card was finished, so nothing more is on its way. Carry on and Loom works on the card from where it stopped — what you answered stays. Your issue hasn't been changed.",
    de: "Sein letzter Schritt endete, bevor die Karte fertig war — es kommt also nichts mehr. Machst du weiter, arbeitet Loom an der Karte weiter, wo es aufgehört hat — was du beantwortet hast, bleibt. Dein Issue wurde nicht verändert.",
  },
  "focus.refine.cardStopped.carryOn": { en: "Carry on", de: "Weitermachen" },
  "focus.refine.stopped.starting": { en: "Starting…", de: "Wird gestartet…" },
  "focus.refine.stopped.orMessage": {
    en: "Or write Loom a message below.",
    de: "Oder schreib Loom unten eine Nachricht.",
  },
  // No workspace at all: the phase is `idle` (no supervisor — Loom's server
  // restarted, or nothing was ever booted for this page) and the server says
  // nothing runs, for a minute. `POST /reconnect` really does boot one here.
  "focus.refine.noWorkspace.headline": {
    en: "Loom's workspace isn't running",
    de: "Looms Arbeitsbereich läuft nicht",
  },
  "focus.refine.noWorkspace.help": {
    en: "Nothing is running for this issue right now. That happens when Loom's server restarted or this page was away for a while. Start the workspace and Loom picks up where it can. Your issue hasn't been changed.",
    de: "Für dieses Issue läuft gerade nichts. Das passiert, wenn Looms Server neu gestartet wurde oder diese Seite eine Weile weg war. Starte den Arbeitsbereich, dann macht Loom weiter, wo es kann. Dein Issue wurde nicht verändert.",
  },
  "focus.refine.noWorkspace.start": {
    en: "Start Loom's workspace",
    de: "Looms Arbeitsbereich starten",
  },
  // The stopped-workspace screen's action: same session, same conversation,
  // same card — the server re-triggers the pipeline and resumes the agent
  // where it can. Shown only when the server says the sandbox is TERMINATED.
  "focus.refine.stalled.reconnect": {
    en: "Restart Loom's workspace",
    de: "Looms Arbeitsbereich neu starten",
  },
  // The restart POST came back 429 / 5xx, or never landed. Nothing has been
  // revoked, so the session and everything answered are still there and the
  // button can simply be pressed again. This used to take the SAME exit a
  // revoked session takes — `startOver`, which drops the binding and lands on
  // the mode picker, under a control whose own copy promises the opposite.
  "focus.refine.stalled.reconnectBusy": {
    en: "Loom couldn’t start the workspace just now — your session and your answers are still here. Try the button again in a moment.",
    de: "Loom konnte den Arbeitsbereich gerade nicht starten — deine Sitzung und deine Antworten sind noch da. Versuch es gleich nochmals.",
  },
  // Demoted to a ghost wherever it appears, and NAMED for what it costs: it
  // mints a new session, so the conversation and the grind are gone.
  "focus.refine.stalled.startOverLossy": {
    en: "Start a new session (loses this conversation)",
    de: "Neue Sitzung starten (verwirft dieses Gespräch)",
  },
  // The sandbox is terminated — a FACT the server states. Before this the body
  // had no branch for it and rendered a spinner under "Loom is stopped…".
  "focus.refine.sandboxDead.headline": {
    en: "Loom's workspace stopped",
    de: "Looms Arbeitsbereich wurde beendet",
  },
  "focus.refine.sandboxDead.help": {
    en: "The workspace Loom was using has shut down, so it can't carry on from here. Restarting it keeps this conversation and everything you've answered.",
    de: "Der Arbeitsbereich, in dem Loom gearbeitet hat, wurde beendet — von hier aus geht es nicht weiter. Ein Neustart behält dieses Gespräch und alles, was du beantwortet hast.",
  },
  "focus.refine.sandboxDead.helpApplyable": {
    en: "The workspace Loom was using has shut down. The card is already complete, so you can apply it now — or restart the workspace to carry on refining.",
    de: "Der Arbeitsbereich, in dem Loom gearbeitet hat, wurde beendet. Die Karte ist bereits vollständig — du kannst sie jetzt übernehmen oder den Arbeitsbereich neu starten, um weiterzuarbeiten.",
  },
  // "Keep going" past the spend ceiling. Names the amount so the user is
  // agreeing to a number, not to an open tab.
  "focus.refine.budget.keepGoing": {
    en: "Keep going (+$5)",
    de: "Weitermachen (+5 $)",
  },
  "focus.refine.budget.keepGoingHelp": {
    en: "Raises this session's limit and picks up where it left off — nothing you've answered is lost.",
    de: "Erhöht das Limit dieser Sitzung und macht dort weiter, wo sie aufgehört hat — nichts von dem, was du beantwortet hast, geht verloren.",
  },
  // One session per ISSUE (`useProjectSession` binds issue work per issue), so
  // "this session" is this issue's session: the spend on this screen is this
  // issue's own.
  "focus.refine.budget.headline": {
    en: "This Loom session ran out of budget",
    de: "Diese Loom-Sitzung hat ihr Budget aufgebraucht",
  },
  "focus.refine.budget.help": {
    en: "Loom reached the spending limit for this session and can't continue here. Raising it carries on exactly where you are; a new session starts this issue from scratch at the same limit.",
    de: "Loom hat das Ausgabelimit dieser Sitzung erreicht und kann hier nicht weitermachen. Wenn du es erhöhst, geht es genau hier weiter; eine neue Sitzung beginnt dieses Issue von vorn — mit demselben Limit.",
  },
  "focus.refine.budget.helpApplyable": {
    en: "Loom reached the spending limit for this session and can't run more steps. Your refined issue is ready, though — you can apply it as-is (this won't run the size review or split), raise the limit, or start this issue over in a new session.",
    de: "Loom hat das Ausgabelimit dieser Sitzung erreicht und kann keine weiteren Schritte ausführen. Dein verfeinertes Issue ist aber fertig — du kannst es so übernehmen (ohne Grössenprüfung oder Aufteilung), das Limit erhöhen oder dieses Issue in einer neuen Sitzung von vorn beginnen.",
  },
  "focus.refine.budget.spent": {
    en: "The session used ${used} of its ${limit} limit.",
    de: "Die Sitzung hat ${used} von ${limit} Limit verbraucht.",
  },
  "focus.refine.budget.applyAnyway": {
    en: "Apply what's ready",
    de: "Fertiges übernehmen",
  },
  // NO unqualified `budget.startOver`. Every terminal refine screen that mints
  // a new session either STAYS on the issue (`newSessionHere`, below) or names
  // the loss it causes (`stalled.startOverLossy`, `fatal.startOver`). A label
  // that says only "Start a new session" is what let the budget wall AND the
  // tools wall silently unwind to the mode picker with a half-refined issue
  // behind them.
  // NAMES the issue, because that is the difference that mattered: this button
  // stays on #{iid} instead of dropping the user on the mode picker with a
  // half-refined issue they then had to find again in the picker.
  "focus.refine.budget.newSessionHere": {
    en: "Start this issue over in a new session",
    de: "Dieses Issue in einer neuen Sitzung von vorn beginnen",
  },
  // Names the CHAT, not the project: the chat holds its own session, so the
  // issues opened in this tab have an untouched budget and a headline that says
  // "this project" would tell the user to stop refining them too. It also has to
  // agree with `budgetSharedNote` two lines below it on the same screen.
  "focus.chat.budgetExhausted": {
    en: "This chat has used up its budget.",
    de: "Dieser Chat hat sein Budget aufgebraucht.",
  },
  // The ceiling is per SESSION, and a project holds two: the issues opened in
  // this browser tab share one, the chat has its own. So the spend above is not
  // this chat alone — it is every message in this chat — and the line has to say
  // which of the two it is talking about.
  // The clauses about the LIMIT keep "Sitzung"/"session" — the ceiling really is
  // per session, and two of them exist. Only the sentence naming the ACT is
  // unified with the button below it, which says "New chat".
  "focus.chat.budgetSharedNote": {
    en: "The limit is per session: the issues you open in this browser tab share one, and a chat has its own. A new chat starts empty.",
    de: "Das Limit gilt pro Sitzung: die Issues, die du in diesem Browser-Tab öffnest, teilen sich eine, ein Chat hat seine eigene. Ein neuer Chat beginnt leer.",
  },
  "focus.chat.budgetPlaceholder": {
    en: "Out of budget — start a new chat below",
    de: "Budget aufgebraucht — starte unten einen neuen Chat",
  },
  // One control, one name for the act — the rail, the budget wall and the ended
  // block all press the same button and all say this.
  "focus.chat.newChat": { en: "New chat", de: "Neuer Chat" },
  // The same disclosure MINUS that promise, for the ended block: a chat that was
  // never spoken to is still `kind=unknown`, so the rail cannot list it and the
  // sentence above would be false.
  "focus.chat.newChatCost": {
    en: "A new chat starts its own workspace — that takes a minute or two and uses budget.",
    de: "Ein neuer Chat startet seinen eigenen Arbeitsbereich — das dauert ein bis zwei Minuten und verbraucht Budget.",
  },

  // --- A paused chat, and the composer holding the words until the row exists ---
  // A paused chat has no card and no button to click through: you type, you
  // send, and the chat wakes up on its own. These lines are the only thing that
  // marks the state, and the cost one is here for the same reason the New-chat
  // control carries `newChatCost` — Send is one keystroke away from a real
  // pipeline, so the price is on screen before it is spent.
  "focus.chat.wakeCost": {
    en: "This chat's workspace is paused. Sending starts it again — that takes a moment and uses budget.",
    de: "Der Arbeitsbereich dieses Chats ist pausiert. Beim Senden startet er wieder — das dauert einen Moment und verbraucht Budget.",
  },
  // The workspace STOPPED under an open chat (GitLab's 60-minute job limit, a
  // cancelled job). Send restarts it, so the cost is disclosed the same way.
  "focus.chat.stoppedCost": {
    en: "This chat's workspace has stopped. Sending starts it again — that takes a moment and uses budget.",
    de: "Der Arbeitsbereich dieses Chats wurde beendet. Beim Senden startet er wieder — das dauert einen Moment und verbraucht Budget.",
  },
  "focus.chat.pausedPlaceholder": {
    en: "Message Loom — sending reopens this chat",
    de: "Nachricht an Loom — Senden öffnet diesen Chat wieder",
  },
  // The four hold captions. They narrate the WAIT; the user's own sentence is
  // still in the box below, so none of them repeats it back.
  "focus.chat.holdWaking": {
    en: "Reconnecting — your message goes out as soon as the chat is back.",
    de: "Verbindet neu — deine Nachricht geht raus, sobald der Chat wieder da ist.",
  },
  "focus.chat.holdSending": {
    en: "Sending — your message shows up above in a moment.",
    de: "Wird gesendet — deine Nachricht erscheint gleich oben.",
  },
  // Deliberately does NOT offer to hand the text back: the frame is already in
  // the outbox under its delivery id, and retyping it would mint a new one and
  // buy a duplicate turn. Retrying redelivers the same id, which the server
  // dedupes.
  //
  // And it does NOT promise the message survives. The outbox drops an entry at
  // its own TTL with nothing but a console line, so "nothing is lost" would be
  // a sentence that goes false while it is still on screen — the same shape as
  // the killed-dialog copy this work deleted. It says what is true: the text is
  // still here, and it has not arrived yet.
  "focus.chat.holdStalled": {
    en: "This hasn't arrived yet. Your message is still here — try again, or start a new chat.",
    de: "Das ist noch nicht angekommen. Deine Nachricht steht noch da — versuche es nochmals oder starte einen neuen Chat.",
  },
  "focus.chat.holdRetry": { en: "Try again", de: "Nochmals versuchen" },
  // The wake was refused, so nothing was ever sent — which is why this one can
  // promise the text is still the user's to send.
  "focus.chat.wakeFailed": {
    en: "Loom couldn't reopen this chat. Your message is still in the box — press Send to try again.",
    de: "Loom konnte diesen Chat nicht wieder öffnen. Deine Nachricht steht noch im Feld — drücke Senden, um es erneut zu versuchen.",
  },
  // The block below the transcript, where the caption above the composer has
  // already said what to press. Short, so the control it introduces stays on
  // screen.
  "focus.chat.wakeFailedTitle": {
    en: "This chat didn't reopen.",
    de: "Dieser Chat liess sich nicht wieder öffnen.",
  },
  // Covers both causes the `expired` status carries — a genuine expiry and a
  // revocation — so it asserts nothing the client cannot know.
  "focus.chat.sessionEnded": {
    en: "This chat has ended and can't be reopened.",
    de: "Dieser Chat ist beendet und lässt sich nicht wieder öffnen.",
  },
  "focus.refine.sessionExpired": {
    en: "This session has expired.",
    de: "Diese Sitzung ist abgelaufen.",
  },
  // Deliberately does NOT offer "start a new session" — a denial follows the
  // identity, so a fresh session is refused identically.
  "focus.refine.sessionDenied": {
    en: "You no longer have access to Loom. Ask an administrator to restore it.",
    de: "Du hast keinen Zugriff mehr auf Loom. Bitte eine Administratorin oder einen Administrator, ihn wiederherzustellen.",
  },
  // The apply-fail screen. The line under this headline is the catalog copy
  // for the failed apply's code: `error.apply.*` in `errors.ts`.
  "focus.refine.applyFail.headline": {
    en: "Couldn’t apply the changes",
    de: "Die Änderungen liessen sich nicht übernehmen",
  },
  "focus.refine.retryApply": { en: "Retry apply", de: "Nochmals übernehmen" },
  // In Retry's place where Retry cannot help (`applyRetryHelps`): a new
  // session on the same issue, so the greens start over and the copy says so.
  "focus.refine.applyFail.refineAgain": {
    en: "Refine this issue again in a new session",
    de: "Dieses Issue in einer neuen Sitzung nochmals verfeinern",
  },
  "focus.refine.openHashN": { en: "Open #{iid}", de: "#{iid} öffnen" },
  "focus.refine.refineAnother": { en: "Refine another", de: "Nächstes verfeinern" },
  "focus.refine.applyTimeout.headline": { en: "Still applying…", de: "Wird noch übernommen…" },
  "focus.refine.applyTimeout.help": {
    en: "This is taking longer than expected. If it doesn’t finish, retry the apply or open the issue to check.",
    de: "Das dauert länger als gedacht. Wenn es nicht durchläuft, versuch es nochmals oder öffne das Issue zur Kontrolle.",
  },
  "focus.refine.applied.headline": { en: "Applied — issue updated", de: "Übernommen — Issue aktualisiert" },
  "focus.refine.applying.headline": {
    en: "Sent to Loom — applying your changes",
    de: "An Loom gesendet — deine Änderungen werden übernommen",
  },
  "focus.refine.applied.help": {
    en: "The refined story, acceptance criteria, and metadata are now on the issue.",
    de: "Die verfeinerte Story, die Akzeptanzkriterien und die Metadaten stehen jetzt am Issue.",
  },
  "focus.refine.applying.help": {
    en: "Loom is writing the refined story, acceptance criteria, and metadata onto the issue.",
    de: "Loom schreibt gerade die verfeinerte Story, die Akzeptanzkriterien und die Metadaten ans Issue.",
  },
  // The implementation section did not make it onto the issue. One key per
  // server reason CODE, so the browser renders local copy from a closed
  // catalogue and no server prose ever crosses. Reached only when THIS surface
  // showed the user a plan — a flow that never ran the tail round has nothing
  // to have lost, and `no_plan` cannot tell those two apart.
  "focus.refine.applied.planDropped.not_confirmed": {
    en: "The implementation notes are not on the issue: the plan was replaced before it was confirmed. Run the refine again if you want them.",
    de: "Die Umsetzungsnotizen stehen nicht am Issue: Der Plan wurde ersetzt, bevor er bestätigt war. Starte die Verfeinerung nochmals, wenn du sie brauchst.",
  },
  "focus.refine.applied.planDropped.criteria_moved": {
    en: "The implementation notes are not on the issue: the acceptance criteria changed after the plan was written, so it no longer described this work.",
    de: "Die Umsetzungsnotizen stehen nicht am Issue: Die Akzeptanzkriterien haben sich nach dem Plan geändert, er beschrieb diese Arbeit also nicht mehr.",
  },
  "focus.refine.applied.planDropped.empty_after_normalize": {
    en: "The implementation notes are not on the issue: the plan came back empty.",
    de: "Die Umsetzungsnotizen stehen nicht am Issue: Der Plan kam leer zurück.",
  },
  "focus.refine.applied.planDropped.unknown": {
    en: "The implementation notes are not on the issue.",
    de: "Die Umsetzungsnotizen stehen nicht am Issue.",
  },
  "focus.refine.boot.headline": { en: "Loom is {phase}…", de: "Loom {phase}…" },
  "focus.refine.boot.help": {
    en: "Setting up to refine “{title}”. Loom reads through your real code, so its questions are about your product and not boilerplate. Anything it needs from you it will ask right here.",
    de: "Loom richtet sich ein, um «{title}» zu verfeinern. Es liest sich in deinen echten Code ein, damit sich die Fragen um dein Produkt drehen und nicht um Boilerplate. Was Loom von dir braucht, fragt es direkt hier.",
  },
  // Composer placeholder while nothing is being asked. Deliberately NOT the
  // default "Or tell Loom in your own words…" — with no question on screen
  // that reads as an invitation to answer one, which is where an unattributable
  // answer comes from. This box adds context; questions get their own screen.
  "focus.refine.composer.addContext": {
    en: "Add anything Loom should know…",
    de: "Ergänze, was Loom wissen sollte…",
  },
  // The composer's SECOND send, offered only while a question is pinned. The box
  // is the "in your own words" channel for whatever is on screen, which is right
  // for an answer and wrong for an objection — those bytes land in a real user
  // turn, so the gate greens them and the apply writes the objection into the
  // issue. This button is the way to say something without it being claimed as
  // the answer; the question stays pinned, because the user still owes it.
  "focus.refine.composer.asComment": {
    en: "Send as a note (doesn’t answer the question)",
    de: "Als Notiz senden (beantwortet die Frage nicht)",
  },
  // Loom's own draft for a field, OFFERED above the empty box instead of typed
  // into it. A pre-filled box is indistinguishable from the user's own draft, so
  // one ⌘Enter would turn Loom's sentence into a real user turn that grounds
  // byte-exact. The label has to say whose words these are, and invite reading.
  "focus.refine.suggest.fromLoom": {
    en: "Loom’s draft — read it before you use it",
    de: "Loom-Entwurf — lies ihn, bevor du ihn übernimmst",
  },
  // ONE click, and it is the user's answer: it SENDS the exact bytes on screen,
  // on the lane a typed answer takes. It used to copy the text into a box and
  // wait for a second click; the label stayed, so nothing new has to be learned.
  // The same block around a SETTLED red's value: Loom's wording, not a draft —
  // the step's kicker says "Loom's wording", and the block under it must not
  // call the same text a draft.
  "focus.refine.suggest.fromLoomWording": {
    en: "Loom’s wording — read it before you use it",
    de: "Formulierung von Loom — lies sie, bevor du sie übernimmst",
  },
  "focus.refine.suggest.adopt": { en: "Use this text", de: "Diesen Text übernehmen" },
  // The only control that opens a box while Loom has an answer.
  "focus.refine.suggest.writeOwn": { en: "Write my own", de: "Selbst formulieren" },
  "focus.refine.suggest.adoptList": { en: "Use this list", de: "Diese Liste übernehmen" },
  "focus.refine.suggest.changeList": { en: "Change the list", de: "Liste anpassen" },
  // After "Write my own": copies Loom's text into the box — the one-word tweak.
  "focus.refine.suggest.startFrom": {
    en: "Start from Loom’s text",
    de: "Mit Loom-Text beginnen",
  },
  // Closes the box and brings the two buttons back. Shown only while the box is
  // empty or still holds Loom's text unchanged, so it never throws typing away.
  "focus.refine.suggest.back": {
    en: "Back to Loom’s draft",
    de: "Zurück zum Loom-Entwurf",
  },
  // Accessible names carry the field: the issue card shows up to six rows, and
  // six buttons all named "Use this text" is one name repeated six times.
  "focus.refine.suggest.adoptAria": {
    en: "Use this text ({label})",
    de: "Diesen Text übernehmen ({label})",
  },
  "focus.refine.suggest.writeOwnAria": {
    en: "Write my own ({label})",
    de: "Selbst formulieren ({label})",
  },
  "focus.refine.suggest.adoptListAria": {
    en: "Use this list ({label})",
    de: "Diese Liste übernehmen ({label})",
  },
  "focus.refine.suggest.changeListAria": {
    en: "Change the list ({label})",
    de: "Liste anpassen ({label})",
  },
  // A bare "yes" typed into an answer that SETS a value (role, means, ends, a
  // list row) is not sent — it names nothing. Shown next to "Use this text",
  // which is the yes the user meant. Never on a yes/no question: there "ja" IS
  // the answer.
  "focus.refine.suggest.assentHint": {
    en: "A yes alone doesn’t fill this in — use Loom’s text or write it out.",
    de: "Ein „Ja“ allein füllt das nicht aus — übernimm den Loom-Text oder schreib es aus.",
  },
  "focus.refine.suggest.assentHintNoDraft": {
    en: "A yes alone doesn’t fill this in — write it out in a few words.",
    de: "Ein „Ja“ allein füllt das nicht aus — schreib es in ein paar Worten aus.",
  },
  "focus.refine.suggest.assentRow": {
    en: "A yes isn’t a criterion — write something that can be checked.",
    de: "Ein „Ja“ ist kein Kriterium — schreib etwas, das sich prüfen lässt.",
  },
  // A red value offered as Loom's answer, once settled: the gate could not find
  // it in the user's words, so it IS Loom's wording — true whether the model
  // made it up or paraphrased the user.
  "focus.refine.loomVersion.kicker": {
    en: "Loom’s wording",
    de: "Formulierung von Loom",
  },
  "focus.refine.loomVersion.question": {
    en: "Loom wrote this version of {label}. Use it as your answer, or write your own?",
    de: "Diese Fassung von {label} hat Loom formuliert. Übernimmst du sie als deine Antwort, oder formulierst du selbst?",
  },
  // The message bar, folded while Loom's answer is closed on screen. It opens a
  // bar that only sends notes — a note claims nothing.
  "focus.refine.composer.openNote": {
    en: "Tell Loom something else",
    de: "Loom etwas anderes sagen",
  },
  "focus.refine.composer.notePlaceholder": {
    en: "A note for Loom — it doesn’t answer the question",
    de: "Eine Notiz an Loom — sie beantwortet die Frage nicht",
  },
  // ─── Choice controls (priority / milestone) ───────────────────────────
  // The option list is a single-choice set, so it is announced as one; without
  // a name a screen reader reads "radio group" and nothing else.
  "focus.refine.choice.groupAria": { en: "Choices", de: "Auswahl" },
  // The option the user picked before — shown when an undo brings the question
  // back, so "Undo — go back" has something to point at instead of returning to
  // twenty identical buttons.
  "focus.refine.choice.yourPick": {
    en: "your previous answer",
    de: "deine vorherige Antwort",
  },
  // The value the readiness card holds for this field right now. Deliberately
  // NOT "what GitLab has": the card slot can hold either a reading of the issue
  // or Loom's proposal, and this surface cannot tell those apart.
  "focus.refine.choice.onCardNow": {
    en: "what Loom has for this now",
    de: "was Loom aktuell dafür hat",
  },
  // What the ISSUE carries right now, on either forge. The only provenance an
  // OPEN question has: an agent `needs_input` slot sends a question and no
  // value, so on the step where the agent itself says "it's currently flagged
  // as Priority 2 — keep that, or change it?" the card had nothing to mark and
  // the three buttons stood unmarked. Distinct wording from `onCardNow` because
  // it is a different claim — this one IS the issue's value, not Loom's
  // reading. It names no forge: the user's projects are just projects.
  "focus.refine.choice.onIssueNow": {
    en: "on the issue now",
    de: "aktuell am Issue",
  },
  "focus.refine.choice.closedMilestone": { en: "closed", de: "geschlossen" },
  // The option Loom's draft (or a settled red) names. One click picks it.
  "focus.refine.choice.loomSuggests": {
    en: "Loom’s suggestion",
    de: "Vorschlag von Loom",
  },
  // The option set is CLOSED and the agent's question does not know that — live
  // it invited keeping "Priority 2" over three buttons reading High / Normal /
  // Low. Says what the buttons can actually do, under the buttons.
  "focus.refine.choice.onlyOptions": {
    en: "These are the only choices — whichever you pick replaces what the issue has now.",
    de: "Mehr Auswahl gibt es nicht — was du wählst, ersetzt den aktuellen Wert am Issue.",
  },
  // Same note once the issue's CURRENT value is marked on one of the options:
  // that option is the "keep it" the agent's question offers, and saying so is
  // what turns a marked row into an answerable choice. Live, "keep that, or
  // change it?" sat over three buttons with no keep among them.
  "focus.refine.choice.onlyOptionsKeep": {
    en: "These are the only choices — pick the marked one to keep what the issue has, or another to replace it.",
    de: "Mehr Auswahl gibt es nicht — wähle den markierten Wert, um ihn zu behalten, oder einen anderen, um ihn zu ersetzen.",
  },
  // ─── Widget chrome ────────────────────────────────────────────────────
  // English strings that used to be hard-coded in widgets.tsx. They stayed
  // English inside an otherwise German flow (the "Priority options render
  // English" class of finding); the EN values are unchanged so nothing that
  // reads them shifts.
  "focus.widget.text.aria": { en: "Your answer", de: "Deine Antwort" },
  "focus.widget.text.placeholder": {
    en: "Type your answer…",
    de: "Antwort eingeben…",
  },
  "focus.widget.list.addAria": { en: "Add an item", de: "Eintrag hinzufügen" },
  "focus.widget.list.addPlaceholder": {
    en: "Add an item…",
    de: "Eintrag hinzufügen…",
  },
  "focus.widget.list.done": { en: "Done", de: "Fertig" },
  "focus.widget.milestone.loading": {
    en: "Loading milestones…",
    de: "Meilensteine werden geladen…",
  },
  // The milestone picker with nothing to pick. No box in either state: the
  // milestone is picked from the project's list or skipped. "Open", because
  // the picker offers open milestones only (a closed one is reached by name
  // through the filter on a long list) and a project whose milestones are all
  // closed lands here too. The error line is the issue page card's
  // (`chatRender.milestonesError`); its empty line lists closed ones as well,
  // so it keeps "No milestones in this project.".
  "focus.widget.milestone.none": {
    en: "No open milestones in this project.",
    de: "Keine offenen Meilensteine in diesem Projekt.",
  },
  "focus.widget.milestone.error": {
    en: "Couldn’t load milestones.",
    de: "Meilensteine liessen sich nicht laden.",
  },
  // The filter over a long milestone list (>8 options). Twenty-one rows in a
  // flat column with hotkeys on the first eight is a scroll, not a choice.
  "focus.widget.milestone.filterAria": {
    en: "Filter milestones by name",
    de: "Meilensteine nach Namen filtern",
  },
  "focus.widget.milestone.filterPlaceholder": {
    en: "Filter by name…",
    de: "Nach Namen filtern…",
  },
  "focus.widget.milestone.noMatch": {
    en: "No milestone matches that.",
    de: "Kein Meilenstein passt dazu.",
  },
  // ─── List builder rows ────────────────────────────────────────────────
  // Per-row controls are numbered: six buttons all called "Remove" (or "Edit")
  // are one accessible name repeated six times.
  "focus.refine.list.editRow": { en: "Edit item {n}", de: "Punkt {n} bearbeiten" },
  "focus.refine.list.removeRow": {
    en: "Remove item {n}",
    de: "Punkt {n} entfernen",
  },
  // The list builder, opened by "Change the list" with Loom's rows IN it. Says
  // whose words are in the list and that changing them is expected — the user is
  // correcting a draft, not confirming a finished answer.
  "focus.refine.suggest.prefilled": {
    en: "Loom’s draft — change, remove or add anything",
    de: "Loom-Entwurf — ändere, entferne oder ergänze, was du willst",
  },
  // A field whose value the gate could not find in any user turn (verdict
  // `red`). Not "you didn't answer" and not the gate's own deny reason (that
  // text is written for the agent) — just the honest statement plus the
  // five-second repair.
  //
  // Two hosts render these, and they must keep saying the same thing:
  //   • Focus (RefineFlow) — on the settled-unverified step, i.e. a field the
  //     user DID answer on a card that read the answer and still failed,
  //   • the readiness card (chat/render/ReadinessCard) — on any `red` row,
  //     because that host has no `provided` state to narrow it with. The
  //     sentence holds either way: it describes what the server did, not what
  //     the user did or didn't do.
  "focus.refine.unverified.kicker": {
    en: "Couldn’t match this to your words",
    de: "Konnte das deinen Worten nicht zuordnen",
  },
  "focus.refine.unverified.question": {
    en: "Loom couldn’t match its version of {label} to your words. Can you write it once more?",
    de: "Loom konnte seine Fassung von {label} deinen Worten nicht zuordnen. Schreibst du es noch einmal?",
  },
  // A field the gate REFUSED on shape rather than on grounding: it found the
  // user's words fine and could not use them in that form (`isRoleShaped`, the
  // DoD length cap). Only reached when the agent has gone idle without repairing
  // its own proposal — the normal case is that it re-quotes a narrower span and
  // the user never learns the gate ran.
  //
  // "Got that" is the load-bearing half of the sentence, not politeness: it is
  // the only thing on the screen that tells the user their answer was kept. A
  // re-ask whose kicker AND headline match the first ask is, from the user's
  // chair, indistinguishable from amnesia — which is precisely what shipped
  // before, in the gate's own English, on a German session. Shares the
  // `unverified` kicker ("Couldn't match this to your words"): a DIFFERENT
  // kicker from the first ask is the signal that this is not the same question
  // coming round again.
  "focus.refine.shape.question": {
    en: "Got that — can you put {label} in a few words?",
    de: "Alles klar — kannst du {label} in wenigen Worten sagen?",
  },
  // Queue hint: "{count} more {noun} after this — …". The {noun} ternary stays
  // (plural.questions is "{count} question(s)" — its word order can't carry the
  // "more <noun>" form), so the singular/plural noun lives here as two keys.
  "focus.refine.queueHint": {
    en: "{count} more {noun} after this — Loom waits for all your answers before it picks back up.",
    de: "Danach noch {count} {noun} — Loom wartet auf all deine Antworten, bevor es weitermacht.",
  },
  "focus.refine.queueHint.one": { en: "question", de: "Frage" },
  "focus.refine.queueHint.other": { en: "questions", de: "Fragen" },
  "focus.refine.review.setHeadline": { en: "Set {label} to…", de: "{label} setzen auf…" },
  "focus.refine.review.setAria": { en: "Set {label}", de: "{label} setzen" },
  "focus.refine.review.keepHeadline": {
    en: "Loom set this for you — keep it?",
    de: "Von Loom vorgeschlagen – so übernehmen?",
  },
  "focus.refine.review.keep": { en: "Keep", de: "Übernehmen" },
  "focus.refine.review.change": { en: "Change", de: "Anpassen" },
  // The value shown in the POSITION it occupies in the story sentence, for the
  // three fields that compose it. A bare "the dashboard is hard to scan when a
  // project has a lot of issues" is a true thing the user said; the same words
  // after "so that" are the opposite of what they asked for, and only the
  // second form makes that visible. This is UI framing around a value that is
  // already on screen — the sentence the issue is actually written from is
  // composed server-side at apply time, not here.
  "focus.refine.review.inStory.role": { en: "As {value}, …", de: "Als {value} …" },
  "focus.refine.review.inStory.means": {
    en: "… I want {value}, …",
    de: "… möchte ich {value} …",
  },
  "focus.refine.review.inStory.ends": {
    en: "… so that {value}.",
    de: "… damit {value}.",
  },
  // The same row for a value that already carries its OWN connective of a class
  // this one cannot stand in front of — "to reduce the support load", "um … zu
  // …". `spliceNarrativeClause` (@loom/shared/readiness) answers which of the
  // two rows applies; stacking them produced "… so that so we are ready to
  // integrate …" on a live English session.
  "focus.refine.review.inStory.endsOwnConnective": {
    en: "… {value}.",
    de: "… {value}.",
  },
  "focus.refine.held.headline": {
    en: "Got it — your answers are ready",
    de: "Alles klar — deine Antworten stehen bereit",
  },
  "focus.refine.held.helpOne": {
    en: "Your answer goes to Loom",
    de: "Deine Antwort geht an Loom",
  },
  "focus.refine.held.helpMany": {
    en: "All {count} answers go to Loom together",
    de: "Alle {count} Antworten gehen zusammen an Loom",
  },
  "focus.refine.held.helpTail": {
    en: " the moment it finishes what it's doing — so it picks back up with the full picture, not halfway through.",
    de: ", sobald es mit dem aktuellen Schritt fertig ist — damit es mit dem vollen Bild weitermacht statt auf halbem Weg.",
  },
  "focus.refine.held.wrappingUp": {
    en: "Loom is wrapping up its current pass…",
    de: "Loom macht den aktuellen Durchgang fertig…",
  },
  "focus.refine.thinking.headline": {
    en: "Checking your answer against the code",
    de: "Deine Antwort wird mit dem Code abgeglichen",
  },
  "focus.refine.thinking.working": {
    en: "Loom is working through that…",
    de: "Loom arbeitet das gerade durch…",
  },
  // A NOTE is not an answer. A send that claimed nothing — no question on
  // screen, or "Send as a note" — used to put "Checking your answer against the
  // code" over a screen that showed neither an answer nor the note, so the user
  // could not tell whether their words had gone anywhere.
  "focus.refine.note.reading": {
    en: "Loom is reading your note",
    de: "Loom liest deine Notiz",
  },
  "focus.refine.note.received": {
    en: "Loom has your note",
    de: "Loom hat deine Notiz",
  },
  "focus.refine.note.receivedHelp": {
    en: "Loom takes it into account and asks you here when it needs something.",
    de: "Loom berücksichtigt sie und fragt dich hier, wenn es etwas braucht.",
  },
  // The label over the note itself, shown as sent under the step.
  "focus.refine.note.sentLabel": { en: "Your note", de: "Deine Notiz" },
  "focus.refine.plan.headline": {
    en: "Working out how this would be built",
    de: "Loom überlegt, wie das umzusetzen wäre",
  },
  "focus.refine.plan.help": {
    // Deliberately no longer ends on "your issue is already ready to apply":
    // that sentence existed to justify an apply-now button that is gone, and
    // read as an invitation to leave on a screen with nothing to leave by.
    en: "Loom is checking your refined issue against the whole codebase so it can size the change and, if it's large, suggest a split. This usually takes a few minutes.",
    de: "Loom prüft dein verfeinertes Issue gegen den gesamten Code, um den Umfang abzuschätzen und bei grossen Änderungen eine Aufteilung vorzuschlagen. Das dauert meist ein paar Minuten.",
  },
  // The SAME wait, re-entered from "Not quite" — the first-pass copy there
  // reads as if the correction was never received, on the one screen where the
  // rejected summary is still the last thing the user read.
  "focus.refine.plan.reworkHeadline": {
    en: "Reworking this with your correction",
    de: "Loom überarbeitet das mit deiner Korrektur",
  },
  "focus.refine.plan.reworkHelp": {
    en: "Loom is going through the codebase again with what you just described, then shows you a fresh summary to check. This usually takes a few minutes.",
    de: "Loom geht den Code mit deiner Beschreibung noch einmal durch und zeigt dir danach eine neue Zusammenfassung zum Prüfen. Das dauert meist ein paar Minuten.",
  },
  "focus.refine.plan.elapsed": {
    en: "Working… {sec}s",
    de: "Läuft… {sec}s",
  },
  "focus.refine.sizewait.headline": {
    en: "Checking the size",
    de: "Der Umfang wird geschätzt",
  },
  "focus.refine.sizewait.help": {
    en: "The plan matches — Loom is finishing the size check so it can suggest a split if this turns out large.",
    de: "Der Plan passt — Loom schliesst noch die Grössenschätzung ab und schlägt bei grossem Umfang eine Aufteilung vor.",
  },
  // The server refused the plan confirmation. NOT the sizing degrade: sizing
  // succeeded on every path that shows these, and `degradedNote` ("couldn't
  // finish sizing") would claim a failure that did not happen. Two causes, two
  // sentences — the server tells them apart and so should the screen.
  "focus.refine.tail.planConfirmSuperseded": {
    // The store moved on while the user was reading: a second
    // `propose_implementation_plan` landed mid-read.
    en: "Loom rewrote the implementation plan while you were reading it, so your confirmation didn't land — the implementation notes won't be added to the issue. Everything else is unaffected.",
    de: "Loom hat den Umsetzungsplan überarbeitet, während du ihn gelesen hast — deine Bestätigung kam deshalb nicht an und die Umsetzungsnotizen kommen nicht ans Issue. Alles andere ist davon nicht betroffen.",
  },
  "focus.refine.tail.planConfirmUnavailable": {
    // Server reasons `no_store` / `no_plan` / `plan_stale` — the plan was gone
    // or no longer described this issue by the time the Yes arrived.
    en: "Loom couldn't record your confirmation of the implementation plan, so the implementation notes won't be added to the issue. Everything else is unaffected.",
    de: "Loom konnte deine Bestätigung des Umsetzungsplans nicht festhalten — die Umsetzungsnotizen kommen deshalb nicht ans Issue. Alles andere ist davon nicht betroffen.",
  },
  "focus.refine.tail.degradedNote": {
    en: "Loom couldn't finish sizing this — you can still apply the issue as it is.",
    de: "Loom konnte die Grössenschätzung nicht abschliessen — du kannst das Issue trotzdem so übernehmen.",
  },
  // The same degrade, told apart by the REASON the server sent. `degradedNote`
  // above is the generic one and stays the fallback for a reason nobody has
  // written copy for yet; these three say what actually went wrong and what it
  // costs, because "couldn't finish" reads as data loss when the issue is in
  // fact untouched. Every one of them says so explicitly.
  "focus.refine.tail.degradedNoSandbox": {
    // Server reason `no_sandbox` / `route_error` — nothing ever ran.
    en: "Loom's workspace isn't available right now, so it couldn't work out a split. Your issue is unchanged — try again in a moment.",
    de: "Der Arbeitsbereich von Loom ist gerade nicht verfügbar, deshalb konnte keine Aufteilung erstellt werden. Dein Ticket bleibt unverändert — versuch es gleich noch einmal.",
  },
  "focus.refine.tail.degradedTimeout": {
    // Server reason `breakdown_timeout` — it ran and did not land in time, so
    // the way out names BOTH options: retry, or take the issue as one ticket.
    en: "Working out a split took too long this time. Your issue is unchanged — you can try again or apply it as one ticket.",
    de: "Die Aufteilung hat diesmal zu lange gedauert. Dein Ticket bleibt unverändert — du kannst es erneut versuchen oder es als ein Ticket übernehmen.",
  },
  "focus.refine.tail.degradedNotGreen": {
    // Server reasons `not_green` / `no_card` / `no_store` — the card moved out
    // from under the offer. No retry is suggested: retrying the same round
    // cannot succeed until the must-haves are green again.
    en: "The must-haves changed since Loom offered the split, so it couldn't draft one. Your issue is unchanged.",
    de: "Die Muss-Kriterien haben sich geändert, seit Loom die Aufteilung angeboten hat — deshalb konnte keine erstellt werden. Dein Ticket bleibt unverändert.",
  },
  // The degrade note's way out. Names the ROUND, not "try again": the issue is
  // already applyable, so this is an optional extra, not a failed step blocking
  // the user. The pending label has to read as "already running" — it is the
  // only thing stopping a second click from spending a second sandbox turn.
  "focus.refine.tail.retrySizing": {
    en: "Try the size check again",
    de: "Grössenschätzung erneut versuchen",
  },
  "focus.refine.tail.retrySizingPending": {
    en: "Checking the size…",
    de: "Grösse wird geschätzt…",
  },
  "focus.refine.tail.noSplitNote": {
    en: "Loom checked the size — this is best built as one ticket, so it wasn't split.",
    de: "Loom hat den Umfang geprüft — das baut sich am besten als ein Ticket, daher keine Aufteilung.",
  },
  "focus.refine.postAccept.degradedNote": {
    en: "Loom couldn't work out a split this time. Your issue is applied and unchanged.",
    de: "Loom konnte diesmal keine Aufteilung erarbeiten. Dein Issue ist übernommen und unverändert.",
  },
  "focus.refine.split.sizeRecommended": {
    en: "Loom sized this {bucket} · ~{files} files — a split is recommended.",
    de: "Loom schätzt dies als {bucket} · ~{files} Dateien — eine Aufteilung wird empfohlen.",
  },
  "focus.refine.split.sizeNotNeeded": {
    en: "Loom sized this {bucket} · ~{files} files — a split isn't needed, but you can split it anyway.",
    de: "Loom schätzt dies als {bucket} · ~{files} Dateien — eine Aufteilung ist nicht nötig, du kannst aber trotzdem aufteilen.",
  },
  // The server rebased the sizing map after the user parked or restored a
  // must-have, and marked the split verdict untrustworthy. Say we do not know
  // rather than reporting a recommendation about a list that no longer exists.
  "focus.refine.split.verdictStale": {
    en: "Your edit changed the must-haves, so Loom's size estimate is out of date. You can still split this if you want to.",
    de: "Deine Änderung hat die Must-haves verschoben, deshalb ist Looms Schätzung nicht mehr aktuell. Du kannst trotzdem aufteilen.",
  },
  // A proposal is already in hand (restored on the bridge attach) but the
  // Accept screen does not render the card — so this is a way back to it, not a
  // request for a new one. It costs no model turn.
  "focus.refine.split.proposalWaiting": {
    en: "Loom already worked out a split for this — it's waiting for you.",
    de: "Loom hat dafür bereits eine Aufteilung erarbeitet — sie wartet auf dich.",
  },
  "focus.refine.split.oversizedSingle": {
    en: "Loom sized this {bucket} · ~{files} files — large, but described as a single outcome. To split it, tell Loom the distinct pieces it should ship as.",
    de: "Loom schätzt dies als {bucket} · ~{files} Dateien — gross, aber als ein einziges Ergebnis beschrieben. Zum Aufteilen: Nenne Loom die einzelnen Teile, die ausgeliefert werden sollen.",
  },
  "focus.refine.split.breakIntoPieces": {
    en: "Break it into pieces",
    de: "In Sub-Issues zerlegen",
  },
  "focus.refine.postAccept.offer": {
    en: "This turned out bigger than expected. Want to split it into sub-issues now?",
    de: "Das ist grösser ausgefallen als erwartet. Jetzt in Sub-Issues aufteilen?",
  },
  "focus.refine.postAccept.dismiss": { en: "No thanks", de: "Nein danke" },
  "focus.refine.validate.headline": {
    en: "Here’s what will change — does this match?",
    de: "Das würde sich ändern — passt das?",
  },
  "focus.refine.validate.help": {
    en: "Before Loom writes anything, check this lands the way you expect. In plain terms — no technical detail.",
    de: "Bevor Loom etwas schreibt: Prüf, ob das so rauskommt, wie du es dir vorstellst. Ganz einfach erklärt, ohne Technik.",
  },
  "focus.refine.validate.yes": { en: "Yes, that’s right", de: "Ja, genau so" },
  "focus.refine.validate.notQuite": { en: "Not quite", de: "Nicht ganz" },
  "focus.refine.correct.headline": {
    en: "What’s different from what you expected?",
    de: "Was ist anders als gedacht?",
  },
  "focus.refine.correct.help": {
    en: "Describe the outcome you have in mind, in your own words. Loom reworks the plan and checks again — no need to mention anything technical.",
    de: "Beschreib in eigenen Worten, was du dir als Ergebnis vorstellst. Loom überarbeitet den Plan und prüft nochmals — Technisches musst du nicht erwähnen.",
  },
  "focus.refine.ready.headline": {
    en: "Everything checks out — ready to apply",
    de: "Alles geprüft — bereit zum Übernehmen",
  },
  // "Open a field only if you want to change it": Accept already keeps every
  // value on screen, so an editor opened "to check" is not a step the user owes.
  "focus.refine.ready.help": {
    en: "This is the refined issue Loom will write. Accept writes these values to the issue — open a field only if you want to change it.",
    de: "So sieht das verfeinerte Issue aus, das Loom schreibt. „Übernehmen & anwenden“ schreibt diese Werte ins Issue — öffne ein Feld nur, wenn du es ändern willst.",
  },
  // The ready editor's way out, in place of "Cancel": it keeps the value.
  "focus.refine.ready.keepAsIs": { en: "Keep as is", de: "So lassen" },
  // A milestone on the ready screen that Accept will NOT write: the project's
  // list has no milestone of that name (renamed or deleted since the pick, or a
  // name nobody picked). Apply resolves it against the same list and leaves the
  // issue's milestone alone, so the screen must not show it as if it lands.
  "focus.refine.ready.milestoneUnchanged": {
    en: "Accept leaves the milestone as it is: this project has no milestone called “{title}”.",
    de: "Übernehmen lässt den Meilenstein, wie er ist: Dieses Projekt hat keinen Meilenstein namens „{title}“.",
  },
  // A bare yes typed into a ready-screen editor is a keep, and says so.
  "focus.refine.ready.keptNote": {
    en: "Nothing changed — this stays as it is.",
    de: "Nichts geändert — das bleibt so.",
  },
  // The issue's TITLE on the review screen. Read-only on purpose: it is not a
  // card field and Accept never writes it, so the copy has to say so — otherwise
  // showing it next to fields Accept DOES write implies the opposite.
  "focus.refine.ready.titleLabel": { en: "Issue name", de: "Name des Issues" },
  "focus.refine.ready.titleNote": {
    en: "Loom named this when the request was filed. Accepting doesn’t change the name — tell Loom in chat if it’s wrong.",
    de: "Diesen Namen hat Loom beim Erfassen vergeben. Übernehmen ändert den Namen nicht — sag es Loom im Chat, wenn er nicht stimmt.",
  },
  // The mirror of `titleNote` on the one row the AGENT wrote: this one Accept
  // does write, and the user can change it right here.
  "focus.refine.ready.dodNote": {
    en: "Loom set this quality bar itself. Accepting writes it to the issue — edit it here if it doesn’t fit.",
    de: "Diesen Qualitätsanspruch hat Loom selbst festgelegt. Mit Übernehmen wird er ins Issue geschrieben — pass ihn hier an, wenn er nicht passt.",
  },
  // The row-level "these are Loom's words, chosen out of yours" notice.
  //
  // ONE key, and the server stamps `revised` for TWO different populations
  // (`propose-card.ts`, merge rule 2) — so this line has to be true of both:
  //
  //   1. `rewroteUserValue` — the model re-quoted a NARROWER span of the same
  //      user turn to get the value past a shape bound ("Oeffentliche
  //      Shop-Kundschaft, die nach wiederverwendbaren Bauteilen sucht" persisted
  //      as "Oeffentliche Shop-Kundschaft"). Here something was shortened.
  //   2. `concernVerdict === "user_revision"` — the value was lifted out of the
  //      user's reply to a concern Loom raised about this very field. Nothing
  //      was shortened; the words are the user's own LATER ones, and the risk is
  //      the opposite one — "honestly whatever you think is best here" is a
  //      deferral, not a value, and no closed vocabulary can tell the two apart.
  //
  // "Loom shortened your wording" was true only of (1) and plainly false on (2),
  // where the reply landing verbatim is the whole point. What both share is the
  // only thing worth saying: the bytes on this row were PICKED BY LOOM out of
  // something the user said, rather than submitted as this field's value — so
  // read them before Accept writes them.
  //
  // Says WHAT happened and points at the control that changes it, in one line.
  // It deliberately does not apologise or ask a question: this is a note on a
  // value the user is about to accept, not a step they owe an answer to, and
  // phrasing it as a question is what would turn it back into the re-ask this
  // whole round is closing.
  //
  // "rather than from an answer to this field" was false of (2): on dev (#143,
  // session 47b36f0b) the user typed "Die Buchhaltung" as the answer to Loom's
  // own question about the User field, and the row said Loom had NOT taken it
  // from an answer to that field. The note now says only what both share.
  "focus.refine.ready.revisedNote": {
    en: "Loom took this wording from something you said. Check it still says what you meant — edit it here if not.",
    de: "Diese Formulierung hat Loom aus etwas übernommen, das du gesagt hast. Prüfe, ob sie noch das Gemeinte trifft — pass sie sonst hier an.",
  },
  // A value answered on the meeting page or its link (refine light): it counts
  // as given; this line says where it came from.
  "focus.refine.ready.meetingAnswerOwner": {
    en: "Answered on the meeting page",
    de: "Auf der Meeting-Seite beantwortet",
  },
  "focus.refine.ready.meetingAnswerLink": {
    en: "Answered through the meeting link",
    de: "Über den Link beantwortet",
  },
  "focus.refine.ready.editAria": { en: "Edit {label}", de: "{label} bearbeiten" },
  "focus.refine.ready.accept": { en: "Accept & apply", de: "Übernehmen & anwenden" },
  "focus.refine.ready.pendingBadge": {
    en: "Folding your edit in…",
    de: "Deine Änderung wird eingearbeitet…",
  },
  "focus.refine.ready.pendingNote": {
    en: "Loom is folding your edit into the issue — accept becomes available when it's done.",
    de: "Loom arbeitet deine Änderung ins Issue ein — Übernehmen ist wieder möglich, sobald das fertig ist.",
  },
  "focus.refine.ready.pendingCancel": {
    en: "Keep the previous value",
    de: "Vorherigen Wert behalten",
  },
  // The comprehension objection frame. Says ONLY that Loom has a doubt and
  // where to look — never what the doubt is. The claim itself is the judge's
  // own sentence, rendered verbatim underneath, because the criterion behind it
  // has been measured firing with reasoning that belongs to a different
  // criterion: copy written from the criterion id would assert a defect the
  // model did not actually find. Advisory — Accept stays armed.
  "focus.refine.ready.comprehension.headline": {
    en: "Loom isn’t sure this reads the way you meant it",
    de: "Loom ist nicht sicher, ob das so gemeint ist",
  },
  "focus.refine.ready.comprehension.help": {
    en: "This doesn’t block anything — you can accept it as it stands. If Loom has a point, edit the field above in your own words first.",
    de: "Das blockiert nichts — du kannst es so übernehmen. Wenn Loom recht hat, pass das Feld oben vorher in deinen eigenen Worten an.",
  },
  // The screens that keep Apply reachable when Loom cannot run (stopped
  // workspace, dead tools, budget wall), while an edit is still unfolded. Apply
  // would be refused, so they offer the keep instead — one button per field.
  "focus.refine.pendingEdit.blocked": {
    en: "An edit you made isn't in the issue yet, so it can't be applied from here. Keep the previous value to apply the issue as it was.",
    de: "Eine deiner Änderungen ist noch nicht im Issue, darum lässt es sich hier nicht übernehmen. Behalte den vorherigen Wert, um das Issue so zu übernehmen, wie es war.",
  },
  "focus.refine.pendingEdit.keep": {
    en: "Keep the previous value: {label}",
    de: "{label}: vorherigen Wert behalten",
  },
  "focus.refine.ready.pendingNoSandbox": {
    en: "The workspace is waking up — your edit is saved and will be folded in as soon as it's back.",
    de: "Die Umgebung startet gerade — deine Änderung ist gespeichert und wird eingearbeitet, sobald sie wieder da ist.",
  },
  "focus.refine.grind.refining": {
    en: "Loom is refining the details…",
    de: "Loom verfeinert die Details…",
  },
  "focus.refine.grind.headline": {
    en: "Tightening the last few points",
    de: "Die letzten Punkte werden geschärft",
  },
  "focus.refine.grind.help": {
    en: "{grounded} of {total} verified. Loom is re-checking the rest against the code.",
    de: "{grounded} von {total} belegt. Loom prüft den Rest nochmals gegen den Code.",
  },
  // The same count while the server says NO turn is running: "re-checking" is
  // only true while one is.
  "focus.refine.grind.count": {
    en: "{grounded} of {total} verified.",
    de: "{grounded} von {total} belegt.",
  },
  // The same screen once nothing is running: Loom has stopped and the open
  // fields are the user's. Names them and the box on this screen they go in.
  "focus.refine.grind.waitingHeadline": {
    en: "A few points are still open",
    de: "Ein paar Punkte sind noch offen",
  },
  "focus.refine.grind.waiting": {
    en: "Loom is waiting for your answer on: {fields}. Write it in the box below.",
    de: "Loom wartet auf deine Antwort zu: {fields}. Schreib sie unten ins Feld.",
  },
  // The receipt's way to the Figma chip. The chip stays under the step, where
  // Connect and Allow live; on a long question that is below the fold, and the
  // receipt above the composer offers to bring it into view.
  "focus.refine.figma.showOne": { en: "Show the Figma link", de: "Figma-Link anzeigen" },
  "focus.refine.figma.showMany": { en: "Show the Figma links", de: "Figma-Links anzeigen" },

  // ─── field.<name>.detail — the "details" disclosure copy ───────────────
  // Completes the field.<name>.* catalog (label/cardLabel/kicker/help/question
  // live in fields.ts); only `detail` was missing. Same key namespace so the
  // locale-aware fieldCopy reads them alongside the other slots.
  "field.role.detail": {
    en: "This is the “who” of the user story. It names the audience the work serves — like “finance manager” or “the nightly billing job” — and everything else is written from their point of view: the capability is what they can do, the outcome is what they gain.",
    de: "Das ist das «Wer» der User Story. Es benennt die Zielgruppe, für die die Arbeit gemacht wird — etwa «Finanzmanager» oder «der nächtliche Abrechnungsjob» — und alles andere wird aus ihrer Sicht geschrieben: das, was sie tun können, und das, was sie davon haben.",
  },
  "field.means.detail": {
    en: "The concrete thing the user should be able to do once this ships, stated as an action rather than a solution — e.g. “export a VAT summary as a PDF”, not “add an export button”. It leaves room for Loom to figure out the how.",
    de: "Das Konkrete, das der Nutzer nach dem Release tun können soll — als Handlung formuliert, nicht als Lösung. Also z. B. «eine USt-Zusammenfassung als PDF exportieren», nicht «einen Export-Button einbauen». So bleibt Loom Raum, das Wie selbst zu finden.",
  },
  "field.ends.detail": {
    en: "The payoff that justifies the work: the value the user gains once they have the capability. It's the “so that…” of the story and the test for whether the feature is actually worth doing.",
    de: "Der Nutzen, der die Arbeit rechtfertigt: was der Nutzer davon hat, sobald er das Ganze nutzen kann. Das ist das «damit…» der Story und zugleich die Probe, ob sich das Feature überhaupt lohnt.",
  },
  "field.acceptance_criteria.detail": {
    en: "A short list of observable conditions that prove the feature works as intended. Each one should be checkable by using the result — not by reading the code.",
    de: "Eine kurze Liste beobachtbarer Bedingungen, die zeigen, dass das Feature wie gewollt funktioniert. Jede sollte sich am fertigen Ergebnis prüfen lassen — nicht am Code.",
  },
  "field.dod_criteria.detail": {
    en: "The standard every issue meets before it ships — your project's existing checks stay green, and Loom adds the right amount of testing for how risky the change is. You're not asked to set it: Loom decides it from your project's setup, shows it on the review screen, and writes it with the issue — you can change it there.",
    de: "Der Standard, den jedes Issue vor dem Release erfüllt — die bestehenden Prüfungen deines Projekts bleiben grün, und Loom ergänzt je nach Risiko der Änderung das passende Mass an Tests. Du musst das nicht festlegen: Loom leitet es aus dem Setup deines Projekts ab, zeigt es auf dem Prüfbildschirm und schreibt es mit dem Issue — dort kannst du es anpassen.",
  },
  "field.story_points.detail": {
    en: "A rough size estimate Loom derives from the surrounding code, used for planning. You're not asked to set it — it's machine-estimated and shown only for reference.",
    de: "Eine grobe Schätzung des Umfangs, die Loom aus dem umliegenden Code ableitet — für die Planung. Du musst sie nicht festlegen; sie wird maschinell geschätzt und nur zur Orientierung gezeigt.",
  },
  "field.milestone.detail": {
    en: "The release or planning checkpoint this issue is grouped under, so it shows up in the right place on the roadmap and in reporting.",
    de: "Der Release oder Planungs-Meilenstein, dem dieses Issue zugeordnet ist — damit es in der Roadmap und im Reporting an der richtigen Stelle auftaucht.",
  },
  "field.priority.detail": {
    en: "How urgently this should be picked up compared with the rest of the backlog. It orders the work — it doesn't change what gets built.",
    de: "Wie dringend das im Vergleich zum Rest des Backlogs angegangen werden soll. Es bestimmt die Reihenfolge der Arbeit — nicht, was gebaut wird.",
  },
  // Generic field-copy fallback (fieldCopy's unknown-field branch).
  "focus.field.fallback.kicker": { en: "Question", de: "Frage" },
  "focus.field.fallback.question": {
    en: "What should the {field} be?",
    de: "Was soll {field} sein?",
  },

  // ─── The paying key's month against its limit (`KeyLimitBanner`) ───────
  "focus.keyLimit.month.project": {
    en: "This project's key: ${used} of ${limit} this month",
    de: "Schlüssel dieses Projekts: ${used} von ${limit} diesen Monat",
  },
  "focus.keyLimit.month.user": {
    en: "Your key: ${used} of ${limit} this month",
    de: "Dein Schlüssel: ${used} von ${limit} diesen Monat",
  },
  "focus.keyLimit.project": {
    en: "This project's key used ${used} of its ${limit} monthly limit.",
    de: "Der Schlüssel dieses Projekts hat ${used} von seinem Monatslimit von ${limit} verbraucht.",
  },
  "focus.keyLimit.user": {
    en: "Your key used ${used} of its ${limit} monthly limit.",
    de: "Dein Schlüssel hat ${used} von seinem Monatslimit von ${limit} verbraucht.",
  },

  // ─── Session history rail ───────────────────────────────────────────────
  // The left drawer that lists this project's earlier CHATS. Keys live under
  // `focus.*` (not `projects.*`) because they describe Focus behaviour — an
  // adoption of the per-project chat binding plus a stage decision — not the
  // dashboard sidebar the same `useSessions` hook also feeds. The whole
  // vocabulary says "Chat" rather than "Unterhaltung": the list holds one kind
  // of conversation, and the broader word claims the others are here too.
  "focus.rail.open": {
    en: "Earlier chats",
    de: "Frühere Chats",
  },
  "focus.rail.close": { en: "Close", de: "Schliessen" },
  // Narrow chrome (≤640px) swaps the toggle's label for this one: the full
  // German string is ~150px next to a status pill, which wraps the chrome on a
  // phone.
  "focus.rail.openShort": { en: "Chats", de: "Chats" },
  "focus.rail.title": {
    en: "Earlier chats",
    de: "Frühere Chats",
  },
  "focus.rail.loadFailed": {
    en: "Couldn't load your earlier chats.",
    de: "Frühere Chats konnten nicht geladen werden.",
  },
  "focus.rail.retry": { en: "Try again", de: "Erneut versuchen" },
  // Scoped to the LIST, never to the user's history. The list holds chats that
  // began after this feature shipped; everything older carries no kind and is
  // hidden, so "du hast noch keine Chats" would be false for a user with fifty
  // of them. "aufgeführt" is deliberate and "gespeichert" is not — the terminal
  // panels below own "nicht mehr gespeichert", which claims deletion.
  "focus.rail.empty": {
    en: "This list shows chats only, for now — and only the ones started since the last update. Conversations about an issue aren't listed here.",
    de: "Diese Liste zeigt vorerst nur Chats — und nur die, die seit dem letzten Update begonnen haben. Unterhaltungen zu einem Issue sind hier nicht aufgeführt.",
  },
  // The rolling-deploy window, and only that: rows came back and none of them
  // carried a kind, which is proof the server dropped the filter. It must not
  // read as an empty history, and it invites the retry the list's own reload
  // already offers.
  "focus.rail.filterUnsupported": {
    en: "This list is briefly out of step with the server. Try again in a moment.",
    de: "Diese Liste ist gerade kurz nicht auf dem Stand des Servers. Versuch es gleich nochmal.",
  },
  "focus.rail.current": { en: "open now", de: "gerade offen" },
  // The warm-sandbox badge, on rows the user is NOT in. "running" rather than
  // "live": next to "open now" on the same row the two read as the same claim,
  // and this one is about the workspace, not about the drawer.
  "focus.rail.live": { en: "running", de: "läuft" },
  // The cost disclosure, said ONCE under the drawer head and said for every
  // row. Nothing on this list can gate it: the rows are a snapshot with no poll
  // and no push, so a warm sandbox can be gone by the time the pointer lands,
  // and that boot is the whole cost. Hence "usually" / "in der Regel" — a row
  // wearing the `focus.rail.live` badge is the one case where the restart does
  // not happen, and a flat claim would contradict the badge beneath it.
  // "Arbeitsbereich" is the product's own word for the sandbox
  // (focus.refine.*); "Budget" carries no owner, because the spend wall is
  // per-sandbox rather than per-person.
  "focus.rail.costNotice": {
    en: "Opening an earlier chat usually restarts its workspace — that can take a minute or two, and it uses budget.",
    de: "Wenn du einen früheren Chat öffnest, wird sein Arbeitsbereich in der Regel neu gestartet — das kann ein bis zwei Minuten dauern und verbraucht Budget.",
  },
  // The rail's own control — the ONE place a second chat can be started without
  // first being inside one. It is what makes this list have a producer: before
  // it, "Mit Loom chatten" always RESUMED the bound chat, so the only way to a
  // second row was a second browser tab.
  //
  // Its disclosure sits beside it rather than in `costNotice` above: that
  // sentence hedges with "in der Regel" because a warm row skips the boot, and a
  // NEW chat has no warm row to skip — it always pays. Two acts, two costs, each
  // named where it is performed.
  "focus.rail.newChatCost": {
    en: "Starts its own workspace — a minute or two, and it uses budget.",
    de: "Startet einen eigenen Arbeitsbereich — ein bis zwei Minuten, verbraucht Budget.",
  },
  "focus.rail.opening": { en: "opening…", de: "wird geöffnet…" },
  // A row with no bound issue and no user turn behind it — its title is still
  // the provisioning placeholder, which is a server clock, not a name.
  "focus.rail.untitled": { en: "Chat", de: "Chat" },
  "focus.rail.today": { en: "Today", de: "Heute" },
  "focus.rail.yesterday": { en: "Yesterday", de: "Gestern" },
  // `GET /sessions` has no offset and no cursor, so 100 is the whole list there
  // is. Retention (30 days) is what bounds it, not this number.
  "focus.rail.capped": {
    en: "Showing your {count} most recent chats.",
    de: "Zeigt deine {count} neuesten Chats.",
  },
  // The one RETRYABLE refusal, and the only one with its own heading rather
  // than a borrowed terminal one: a network blip announced as "this
  // conversation is finished" over a Try-again button is a permanent claim
  // about a healthy conversation.
  "focus.rail.openFailedTitle": {
    en: "Couldn't open that conversation",
    de: "Diese Unterhaltung liess sich nicht öffnen",
  },
  // Says what the heading above it CANNOT: that nothing is lost and the row is
  // still good. A body that repeats its own heading ("Couldn't open that
  // conversation" twice, live) spends the one line that could have told the
  // reader this is a blip rather than a verdict — the same heading/body
  // division `goneBody` and `endedBody` keep.
  "focus.rail.openFailed": {
    en: "The chat is still there — this was a hiccup reaching it. Try again in a moment.",
    de: "Der Chat ist weiterhin da — es hat nur gerade nicht geklappt, ihn zu erreichen. Versuch es gleich noch einmal.",
  },
  "focus.rail.endedTitle": {
    en: "This conversation is finished",
    de: "Diese Unterhaltung ist abgeschlossen",
  },
  // No issue clause here or in `goneBody`: this list is `kind=chat` by
  // construction, so a chat has normally never touched an issue and naming one
  // is a false reassurance. Observed live — the revoked panel told a tester
  // their issue was safe for a chat that never had one. `issueOnlyBody` below
  // keeps its issue sentence: that refusal fires precisely because the server
  // derived a `flowIssueIid` for the row.
  "focus.rail.endedBody": {
    en: "It has been closed for good and can't be reopened. Start a new one instead.",
    de: "Diese Unterhaltung wurde endgültig geschlossen und lässt sich nicht wieder öffnen. Starte stattdessen eine neue.",
  },
  // The OTHER terminal refusal: the row is listed, but its transcript is past
  // the retention horizon and no longer stored. The 30 days mirror
  // `LOOM_SESSION_INDEX_TTL_DAYS`, which the content stores share — change one
  // and this sentence is wrong.
  "focus.rail.goneTitle": {
    en: "This conversation is no longer stored",
    de: "Diese Unterhaltung ist nicht mehr gespeichert",
  },
  "focus.rail.goneBody": {
    en: "Loom keeps conversations for 30 days, and this one is past that — there is nothing left to reopen.",
    de: "Loom bewahrt Unterhaltungen 30 Tage lang auf, und diese ist älter — es ist nichts mehr da zum Öffnen.",
  },
  "focus.rail.rowGone": {
    en: "no longer stored",
    de: "nicht mehr gespeichert",
  },
  // The other two row states a refusal leaves behind. Same slot, same source as
  // the "running" / "open now" badges, so a row can only ever say one of them.
  "focus.rail.rowEnded": {
    en: "finished",
    de: "abgeschlossen",
  },
  "focus.rail.rowIssue": {
    en: "about an issue",
    de: "zu einem Issue",
  },
  // The fourth terminal refusal, and its own heading: the row reached a
  // chat-only list, but the server derives an active issue flow for it, so the
  // stored label is stale. Nothing is lost and nothing is broken — the landing
  // for an issue conversation is simply not offered here yet, which is why it
  // borrows neither the finished-conversation heading nor the retryable one.
  "focus.rail.issueOnlyTitle": {
    en: "That one is about an issue",
    de: "Das ist eine Unterhaltung zu einem Issue",
  },
  // Carries the consequence and where the work still lives, never a restatement
  // of the heading above it — the same division `goneBody` and `endedBody` keep.
  "focus.rail.issueOnlyBody": {
    en: "Reopening those isn't available here yet. You'll still find the issue it worked on in your project.",
    de: "Solche lassen sich hier noch nicht wieder öffnen. Das Issue, an dem sie gearbeitet hat, findest du weiterhin im Projekt.",
  },
  // The fifth refusal, and the only one about the READER rather than the row.
  // `GET /sessions/:id` runs `requireSession` and nothing else, so the rail's
  // 401 is always a lapsed Loom sign-in — never the `reauth_required` dialect,
  // which is a dead GitLab token with its own in-place recovery. That is what
  // lets this name the cause instead of hedging.
  "focus.rail.signedOutTitle": {
    en: "You've been signed out",
    de: "Du bist abgemeldet",
  },
  "focus.rail.signedOutBody": {
    en: "Your sign-in to Loom has lapsed, so this chat can't be opened. Sign in again — your conversations are kept.",
    de: "Deine Anmeldung bei Loom ist abgelaufen, darum lässt sich dieser Chat nicht öffnen. Melde dich neu an — deine Unterhaltungen bleiben erhalten.",
  },
  "focus.rail.signIn": { en: "Sign in again", de: "Neu anmelden" },
  "focus.rail.endedDismiss": { en: "Back to the list", de: "Zurück zur Liste" },
  // ─── Figma links (focus/figma/*) ───────────────────────────────────────
  // Loom reads a pasted Figma design through the user's own Figma account. The
  // catalog rule this block exists to hold: EVERY state in `FigmaLinkState` has
  // its own sentence, and NONE of them says "Figma error." A state the reader
  // cannot act on is a state that should not have been surfaced.
  "focus.figma.fileFallback": { en: "Figma file", de: "Figma-Datei" },

  // --- The connect card ---
  "focus.figma.connectTitle": {
    en: "Loom can read this design",
    de: "Loom kann dieses Design lesen",
  },
  "focus.figma.connectBody": {
    en: "Connect your Figma account and Loom reads the frame you linked — its layout, its texts and the comments people left on it.",
    de: "Verbinde dein Figma-Konto, dann liest Loom den verlinkten Rahmen — den Aufbau, die Texte und die Kommentare, die dort hinterlassen wurden.",
  },
  // The seat wall, said BEFORE the connect rather than on the twentieth read.
  // Figma exposes no seat, no plan and no organisation on any endpoint Loom is
  // allowed to call — `GET /v1/me` is id, handle, picture, e-mail — so the
  // class is undetectable until a refusal announces it. Up front is the only
  // honest place this sentence can stand.
  // No number and no month: a refusal names neither, so the chip never can
  // either, and a promise here would be the one sentence the chip contradicts.
  "focus.figma.connectSeat": {
    en: "One thing up front: Figma limits how many designs Loom may read for you. How many depends on your Figma seat and plan — a View or Collab seat gets far fewer than a Dev or Full seat. Loom can't see which seat you have.",
    de: "Eines vorweg: Figma begrenzt, wie viele Designs Loom für dich lesen darf. Wie viele, hängt von deinem Figma-Sitzplatz und Plan ab — ein View- oder Collab-Sitzplatz bekommt deutlich weniger als ein Dev- oder Full-Sitzplatz. Loom sieht nicht, welchen Sitzplatz du hast.",
  },
  "focus.figma.connect": { en: "Connect Figma", de: "Figma verbinden" },
  "focus.figma.reconnect": {
    en: "Reconnect Figma",
    de: "Figma neu verbinden",
  },
  "focus.figma.later": { en: "Later", de: "Später" },
  "focus.figma.showAgain": { en: "Show again", de: "Wieder anzeigen" },
  "focus.figma.hideAgain": { en: "Put it away", de: "Wieder zur Seite legen" },
  "focus.figma.allowFile": {
    en: "Allow this file for this project",
    de: "Diese Datei für dieses Projekt erlauben",
  },

  // --- Coming back from Figma. The callback's whole vocabulary is four machine
  // words; each gets a line here, and a success needs none because the chips
  // change state on their own. ---
  "focus.figma.result.accessDenied": {
    en: "You stopped the Figma connection. Nothing was changed.",
    de: "Du hast die Figma-Verbindung abgebrochen. Es wurde nichts geändert.",
  },
  "focus.figma.result.oauthError": {
    en: "Figma turned the connection down. You can try again.",
    de: "Figma hat die Verbindung abgelehnt. Du kannst es noch einmal versuchen.",
  },
  "focus.figma.result.exchangeFailed": {
    en: "Loom couldn't finish the connection with Figma. Try again — this is usually temporary.",
    de: "Loom konnte die Verbindung mit Figma nicht abschliessen. Versuch es noch einmal — das ist meist vorübergehend.",
  },

  // --- One sentence per state ---
  "focus.figma.state.recognized": {
    en: "Loom spotted a Figma link here.",
    de: "Loom hat hier einen Figma-Link entdeckt.",
  },
  // Short on purpose: the card above it carries the offer, the seat sentence
  // and both buttons.
  "focus.figma.state.notConnected": {
    en: "Not connected yet, so Loom can't open it.",
    de: "Noch nicht verbunden, darum kann Loom ihn nicht öffnen.",
  },
  "focus.figma.state.declined": {
    en: "Set aside — Loom is not reading this one.",
    de: "Zur Seite gelegt — Loom liest diesen nicht.",
  },
  "focus.figma.state.declinedOpen": {
    en: "You set this link aside. Connecting Figma reads it after all.",
    de: "Du hast diesen Link zur Seite gelegt. Wenn du Figma verbindest, liest Loom ihn doch.",
  },
  "focus.figma.state.unsupportedSlides": {
    en: "Loom can't read Figma Slides — only design files and single frames.",
    de: "Figma Slides kann Loom nicht lesen — nur Design-Dateien und einzelne Rahmen.",
  },
  "focus.figma.state.unsupportedCommunity": {
    en: "This is a Community file, which Loom can't open.",
    de: "Das ist eine Community-Datei, die Loom nicht öffnen kann.",
  },
  "focus.figma.state.unsupportedCommunityBody": {
    en: "Duplicate it into your own drafts in Figma and paste the link to your copy.",
    de: "Dupliziere sie in Figma in deine eigenen Entwürfe und füge den Link zu deiner Kopie ein.",
  },
  "focus.figma.state.unsupportedOther": {
    en: "Loom can't read this kind of Figma link. A link to a design file, or to a single frame in one, works.",
    de: "Diese Art von Figma-Link kann Loom nicht lesen. Ein Link auf eine Design-Datei oder auf einen einzelnen Rahmen darin funktioniert.",
  },
  // The per-project gate refused the file. This is the one refusal with a
  // button behind it, so it says what the button does before it is pressed.
  "focus.figma.state.notAllowed": {
    en: "This file isn't on this project's list of allowed Figma files.",
    de: "Diese Datei steht nicht auf der Liste der erlaubten Figma-Dateien dieses Projekts.",
  },
  "focus.figma.state.notAllowedBody": {
    en: "Allowing it writes one line into the project's .loom.yml, so everyone working on this project can use the file too.",
    de: "Beim Erlauben schreibt Loom eine Zeile in die .loom.yml des Projekts — dann können alle, die an diesem Projekt arbeiten, die Datei ebenfalls nutzen.",
  },
  "focus.figma.state.loading": {
    en: "Loom is reading this design…",
    de: "Loom liest dieses Design…",
  },
  "focus.figma.state.read": {
    en: "Loom read this design.",
    de: "Loom hat dieses Design gelesen.",
  },
  // Figma answers 404 — not 403 — when an authenticated caller has no access to
  // a file that exists. It is an acknowledged, unfixed bug, so Loom genuinely
  // cannot tell the two causes apart: naming only one would be wrong about half
  // the time.
  "focus.figma.state.unreachable": {
    en: "Loom can't open this file. Either the link is no longer valid, or the Figma account Loom is connected to has no access to it.",
    de: "Loom kann diese Datei nicht öffnen. Entweder ist der Link nicht mehr gültig, oder das mit Loom verbundene Figma-Konto hat keinen Zugriff darauf.",
  },
  "focus.figma.state.unreachableRemedy": {
    en: "Figma answers the same way in both cases. Open the link in Figma yourself: if it opens there, Loom is connected to a different account than the one with access — reconnect with that one. If it doesn't open, the link is stale or the file was never shared with you; ask whoever owns it.",
    de: "Figma antwortet in beiden Fällen gleich. Öffne den Link selbst in Figma: geht er dort auf, ist Loom mit einem anderen Konto verbunden als dem mit Zugriff — verbinde dich mit diesem neu. Geht er nicht auf, ist der Link veraltet oder die Datei wurde nie mit dir geteilt; frag die Person, der sie gehört.",
  },
  // The file opened; the frame did not. Not the account and not access, so the
  // remedy is a fresh link to the frame, never "reconnect".
  "focus.figma.state.frameMissing": {
    en: "Loom opened this file, but the frame your link points to is empty or no longer there.",
    de: "Loom hat die Datei geöffnet, aber der Rahmen aus deinem Link ist leer oder nicht mehr da.",
  },
  "focus.figma.state.frameMissingBody": {
    en: "In Figma, select the frame you mean, copy its link again and paste it here.",
    de: "Wähl in Figma den Rahmen aus, den du meinst, kopier seinen Link neu und füg ihn hier ein.",
  },
  "focus.figma.state.rateLimited": {
    en: "Figma is slowing Loom down right now. Ask again in {seconds} s.",
    de: "Figma bremst Loom gerade. Frag in {seconds} s noch einmal.",
  },
  "focus.figma.state.rateLimitedSoon": {
    en: "Figma is slowing Loom down right now. Ask again in a moment.",
    de: "Figma bremst Loom gerade. Frag gleich noch einmal.",
  },
  "focus.figma.state.tokenExpired": {
    en: "Your Figma connection has run out.",
    de: "Deine Figma-Verbindung ist abgelaufen.",
  },
  "focus.figma.state.tokenExpiredBody": {
    en: "A Figma connection lasts about 90 days. Reconnecting is one click and leaves everything else as it is.",
    de: "Eine Figma-Verbindung hält rund 90 Tage. Das Neuverbinden ist ein Klick, alles andere bleibt, wie es ist.",
  },
  "focus.figma.state.degraded": {
    en: "Loom read only part of this design.",
    de: "Loom hat dieses Design nur teilweise gelesen.",
  },
  "focus.figma.state.degradedBody": {
    en: "The file's name and size came through, its contents didn't. Ask for the design again in a message and Loom has another go.",
    de: "Name und Grösse der Datei kamen an, der Inhalt nicht. Frag in einer Nachricht noch einmal nach dem Design, dann versucht Loom es erneut.",
  },
  // The one generic line in the block — and it is not a failure. It fires when
  // the server reports a state this build of the page has never heard of, which
  // is a deploy skew, so it says the true thing: reload.
  "focus.figma.state.unknown": {
    en: "Loom reports something about this link that this page doesn't know yet.",
    de: "Loom meldet zu diesem Link etwas, das diese Seite noch nicht kennt.",
  },
  "focus.figma.state.unknownBody": {
    en: "Reload the page to get the current version.",
    de: "Lade die Seite neu, um die aktuelle Version zu bekommen.",
  },

  // --- Facts under a chip that has been read. `Label: n` rather than "n
  // Rahmen": that form is plural-neutral in both locales, and plurals.ts is
  // owned elsewhere. ---
  "focus.figma.meta.folder": { en: "Folder", de: "Ordner" },
  "focus.figma.meta.frames": { en: "Frames", de: "Rahmen" },
  "focus.figma.meta.texts": { en: "Text layers", de: "Textebenen" },
  "focus.figma.meta.comments": { en: "Comments", de: "Kommentare" },
  "focus.figma.meta.updated": { en: "Updated", de: "Aktualisiert" },
  // Where the design came from, when Figma was not called for it. "copy" /
  // "Kopie" rather than "cache" / "Zwischenspeicher" on purpose: the user is
  // being told what they are looking at, not how it is stored, and the whole
  // point of the line is that it reads as plainly as the facts beside it.
  // Renders as `From Loom's copy · read 2 days ago`.
  "focus.figma.meta.fromStore": {
    en: "From Loom's copy",
    de: "Aus Looms Kopie",
  },
  "focus.figma.meta.storeReadAt": { en: "read {when}", de: "{when} gelesen" },

  // --- The account's connection (components/FigmaConnection.tsx). The chips
  // speak about one link; these speak about the account behind all of them.
  // Connect / Reconnect reuse the card's button words above. ---
  "focus.figma.connection.notConnected": {
    en: "Figma isn't connected.",
    de: "Figma ist nicht verbunden.",
  },
  "focus.figma.connection.connected": {
    en: "Figma is connected.",
    de: "Figma ist verbunden.",
  },
  "focus.figma.connection.connectedAs": {
    en: "Figma is connected as {handle}.",
    de: "Figma ist mit {handle} verbunden.",
  },
  // The row exists and reads nothing: the access token ran out and nothing is
  // left to renew it. Reconnect sits right beside this line.
  "focus.figma.connection.lapsed": {
    en: "Your Figma connection has run out, so Loom can't read designs.",
    de: "Deine Figma-Verbindung ist abgelaufen, darum kann Loom keine Designs lesen.",
  },
  "focus.figma.connection.disconnect": { en: "Disconnect", de: "Trennen" },
  // The row's Disconnect trigger, named for what it ends: the page shows two.
  "focus.figma.connection.disconnectLabel": {
    en: "Disconnect Figma",
    de: "Figma trennen",
  },
  "focus.figma.connection.disconnecting": {
    en: "Disconnecting…",
    de: "Wird getrennt…",
  },
  "focus.figma.connection.disconnected": {
    en: "Loom no longer uses your Figma account.",
    de: "Loom nutzt dein Figma-Konto nicht mehr.",
  },
  // Figma publishes no way for Loom to withdraw its own access, so the server
  // deletes Loom's copy and says so. This names where the user can finish it.
  // The menu names stay English: Figma's own settings are.
  "focus.figma.connection.figmaStillLists": {
    en: "Figma still lists Loom under Settings → Security → Connected apps. Choose Revoke access there if Figma should forget it too.",
    de: "In Figma steht Loom noch unter Settings → Security → Connected apps. Wähl dort Revoke access, wenn auch Figma es vergessen soll.",
  },
  "focus.figma.connection.disconnectFailed": {
    en: "Loom couldn't disconnect Figma. Try again.",
    de: "Loom konnte Figma nicht trennen. Versuch es noch einmal.",
  },

  // --- States a read can end in that say what to do next ---
  "focus.figma.state.tooLarge": {
    en: "This frame is too big for Loom to read in one go.",
    de: "Dieser Rahmen ist zu gross, als dass Loom ihn auf einmal lesen könnte.",
  },
  "focus.figma.state.tooLargeBody": {
    en: "Link a smaller frame inside it — a single view or section works best.",
    de: "Verlinke einen kleineren Rahmen darin — eine einzelne Ansicht oder ein Abschnitt geht am besten.",
  },
  "focus.figma.state.upstreamError": {
    en: "Figma had a problem answering Loom. Ask again in a moment.",
    de: "Figma hatte ein Problem, Loom zu antworten. Frag gleich noch einmal.",
  },
  "focus.figma.state.configMalformed": {
    en: "The Figma settings in this project's .loom.yml can't be read, so Loom can't open Figma files here.",
    de: "Die Figma-Einstellungen in der .loom.yml dieses Projekts lassen sich nicht lesen, darum kann Loom hier keine Figma-Dateien öffnen.",
  },
  "focus.figma.state.configMalformedBody": {
    en: "Someone who looks after this project needs to fix the figma: section in .loom.yml.",
    de: "Jemand, der dieses Projekt betreut, muss den Abschnitt figma: in der .loom.yml reparieren.",
  },

  // --- "Allow this file": what happened after the click ---
  "focus.figma.allow.allowed": {
    en: "Allowed for this project. Loom may read this design now — ask about it in the chat.",
    de: "Für dieses Projekt erlaubt. Loom darf dieses Design jetzt lesen — frag im Chat danach.",
  },
  "focus.figma.allow.refused.noAllowlist": {
    en: "This project no longer has a list of allowed Figma files, so there is nothing to add this one to.",
    de: "Dieses Projekt hat keine Liste erlaubter Figma-Dateien mehr, darum gibt es nichts, wo Loom diese Datei eintragen könnte.",
  },
  "focus.figma.allow.refused.noAllowlistBody": {
    en: "Without a list, every file is allowed. Ask about the design again in the chat.",
    de: "Ohne Liste sind alle Dateien erlaubt. Frag im Chat noch einmal nach dem Design.",
  },
  "focus.figma.allow.refused.accessDenied": {
    en: "Loom couldn't allow this file: you may not change this project's settings.",
    de: "Loom konnte diese Datei nicht erlauben: Du darfst die Einstellungen dieses Projekts nicht ändern.",
  },
  "focus.figma.allow.refused.accessDeniedBody": {
    en: "Ask someone who looks after this project to allow it for you.",
    de: "Bitte jemanden, der dieses Projekt betreut, sie für dich zu erlauben.",
  },
  "focus.figma.allow.refused.writeFailed": {
    en: "Loom couldn't write this file into the project's .loom.yml. Nothing was changed.",
    de: "Loom konnte diese Datei nicht in die .loom.yml des Projekts schreiben. Es wurde nichts geändert.",
  },
  "focus.figma.allow.refused.concurrentWrite": {
    en: "Someone changed the project's .loom.yml at the same moment, so Loom stopped. Nothing was changed.",
    de: "Jemand hat die .loom.yml des Projekts im selben Moment geändert, darum hat Loom abgebrochen. Es wurde nichts geändert.",
  },
  "focus.figma.allow.refused.preconditionUnavailable": {
    en: "Loom couldn't tell which version of the project's .loom.yml it would change, so it wrote nothing.",
    de: "Loom konnte nicht erkennen, welche Version der .loom.yml es ändern würde, darum hat es nichts geschrieben.",
  },
  // Neither of these two can work on a second click, so neither says "try
  // again". Each names the one thing that does help.
  "focus.figma.allow.refused.unavailable": {
    en: "Loom can't change this project's settings from here, so the file wasn't added.",
    de: "Loom kann die Einstellungen dieses Projekts von hier aus nicht ändern, darum wurde die Datei nicht eingetragen.",
  },
  "focus.figma.allow.refused.unavailableBody": {
    en: "Someone who looks after this project can add the file to the figma: section in .loom.yml by hand.",
    de: "Jemand, der dieses Projekt betreut, kann die Datei von Hand im Abschnitt figma: der .loom.yml eintragen.",
  },
  "focus.figma.allow.refused.unknownLink": {
    en: "Loom no longer knows which file this link is, so it couldn't allow it.",
    de: "Loom weiss nicht mehr, welche Datei hinter diesem Link steckt, darum konnte es sie nicht erlauben.",
  },
  "focus.figma.allow.refused.unknownLinkBody": {
    en: "Paste the link into the chat again, and Loom picks it up from there.",
    de: "Füg den Link noch einmal in den Chat ein, dann nimmt Loom ihn von dort auf.",
  },
  "focus.figma.allow.retryBody": {
    en: "You can try again in a moment.",
    de: "Du kannst es gleich noch einmal versuchen.",
  },
  // ─── Figma limits (focus/figma/limitCopy.ts) ──────────────────────────
  // A short limit past 90 s reads in minutes or hours, never a count of seconds.
  "focus.figma.limit.rateLimitedMinutes": {
    en: "Figma is slowing Loom down right now. Ask again in about {minutes} min.",
    de: "Figma bremst Loom gerade. Frag in etwa {minutes} Min. noch einmal.",
  },
  "focus.figma.limit.rateLimitedHours": {
    en: "Figma is slowing Loom down. Ask again in about {hours} h.",
    de: "Figma bremst Loom. Frag in etwa {hours} Std. noch einmal.",
  },
  // A long block names the reset Figma gave, as a date and time. It claims no
  // cap size and no month boundary: a 429 says neither.
  "focus.figma.limit.blockedUntil": {
    en: "Figma won't let Loom read any more designs until {when}.",
    de: "Figma lässt Loom bis {when} keine weiteren Designs lesen.",
  },
  "focus.figma.limit.blocked": {
    en: "Figma won't let Loom read any more designs for now, and didn't say until when.",
    de: "Figma lässt Loom vorerst keine weiteren Designs lesen und hat nicht gesagt, bis wann.",
  },
  "focus.figma.limit.blockedOver": {
    en: "Figma's limit should be over now. Ask Loom again.",
    de: "Das Limit von Figma sollte jetzt vorbei sein. Frag Loom noch einmal.",
  },
  "focus.figma.limit.blockedBody": {
    en: "Figma sets this limit for your account, and it depends on your Figma seat and plan. Asking again sooner won't help.",
    de: "Figma legt dieses Limit für dein Konto fest, abhängig von deinem Figma-Sitzplatz und Plan. Früher noch einmal fragen hilft nicht.",
  },

  // --- A chip never forgets what it knew (focus/figma/*) ---
  // A link to a whole file. Loom reads one frame per call, and the scan knows
  // that before any read. Figma's menu item stays English, like its settings.
  "focus.figma.state.needsFrame": {
    en: "This link opens the whole file, and Loom reads one frame at a time.",
    de: "Dieser Link öffnet die ganze Datei, Loom liest aber immer nur einen Rahmen.",
  },
  "focus.figma.state.needsFrameBody": {
    en: "In Figma, right-click the frame you mean, choose Copy link to selection, and paste that link here.",
    de: "Klick in Figma mit der rechten Maustaste auf den Rahmen, den du meinst, wähl Copy link to selection und füg den Link hier ein.",
  },
  // Chip names before a read. Figma opened the file of a `frame_missing` chip,
  // so what it lacks is a frame; several links in one message get their place.
  "focus.figma.state.frameFallback": { en: "Figma frame", de: "Figma-Rahmen" },
  "focus.figma.state.linkInMessage": {
    en: "Figma link {index} of {count} in your message",
    de: "Figma-Link {index} von {count} in deiner Nachricht",
  },
  "focus.figma.state.linksSkipped": {
    en: "This message has more Figma links than Loom looks at in one go. Left out: {count}. Send those in a new message.",
    de: "Diese Nachricht enthält mehr Figma-Links, als Loom auf einmal anschaut. Ausgelassen: {count}. Schick diese in einer neuen Nachricht.",
  },
  // Every node in a frame is a layer in Figma's own words.
  "focus.figma.state.metaLayers": { en: "Layers", de: "Ebenen" },
  // One quiet line under a design Loom read, when reading it AGAIN was refused.
  // The read's facts stay on the chip, so each line starts from that attempt.
  "focus.figma.state.refresh.rateLimited": {
    en: "Loom tried to read it again, but Figma is limiting how often Loom may read.",
    de: "Loom wollte es noch einmal lesen, aber Figma begrenzt gerade, wie oft Loom lesen darf.",
  },
  "focus.figma.state.refresh.rateLimitedUntil": {
    en: "Loom tried to read it again, but Figma is limiting how often Loom may read. Ask again {when}.",
    de: "Loom wollte es noch einmal lesen, aber Figma begrenzt gerade, wie oft Loom lesen darf. Frag {when} noch einmal.",
  },
  // A long block: a 429 names neither the seat nor a month, only (maybe) a reset.
  "focus.figma.state.refresh.seatLimited": {
    en: "Loom tried to read it again, but Figma won't let Loom read any more designs for now.",
    de: "Loom wollte es noch einmal lesen, aber Figma lässt Loom vorerst keine weiteren Designs lesen.",
  },
  "focus.figma.state.refresh.seatLimitedUntil": {
    en: "Loom tried to read it again, but Figma won't let Loom read any more designs until {when}.",
    de: "Loom wollte es noch einmal lesen, aber Figma lässt Loom bis {when} keine weiteren Designs lesen.",
  },
  // Either limit, once the time it named has passed. It no longer says the
  // limit stands, and it does not promise the next read will work.
  "focus.figma.state.refresh.limitOver": {
    en: "Loom tried to read it again, but Figma didn't allow it then. That limit should be over now. Ask Loom again.",
    de: "Loom wollte es noch einmal lesen, aber Figma hat es da nicht erlaubt. Das Limit sollte jetzt vorbei sein. Frag Loom noch einmal.",
  },
  "focus.figma.state.refresh.tokenExpired": {
    en: "Loom tried to read it again, but your Figma connection has run out.",
    de: "Loom wollte es noch einmal lesen, aber deine Figma-Verbindung ist abgelaufen.",
  },
  "focus.figma.state.refresh.notConnected": {
    en: "Loom tried to read it again, but Figma isn't connected any more.",
    de: "Loom wollte es noch einmal lesen, aber Figma ist nicht mehr verbunden.",
  },
  "focus.figma.state.refresh.notAllowed": {
    en: "Loom tried to read it again, but this file is no longer on this project's list of allowed Figma files.",
    de: "Loom wollte es noch einmal lesen, aber diese Datei steht nicht mehr auf der Liste der erlaubten Figma-Dateien dieses Projekts.",
  },
  "focus.figma.state.refresh.configMalformed": {
    en: "Loom tried to read it again, but the Figma settings in this project's .loom.yml can't be read.",
    de: "Loom wollte es noch einmal lesen, aber die Figma-Einstellungen in der .loom.yml dieses Projekts lassen sich nicht lesen.",
  },
  "focus.figma.state.refresh.unreachable": {
    en: "Loom tried to read it again, but Figma no longer opens the file for Loom.",
    de: "Loom wollte es noch einmal lesen, aber Figma öffnet die Datei für Loom nicht mehr.",
  },
  "focus.figma.state.refresh.frameMissing": {
    en: "Loom tried to read it again, but the frame is now empty or gone.",
    de: "Loom wollte es noch einmal lesen, aber der Rahmen ist jetzt leer oder weg.",
  },
  "focus.figma.state.refresh.tooLarge": {
    en: "Loom tried to read it again, but the frame has grown too big to read in one go.",
    de: "Loom wollte es noch einmal lesen, aber der Rahmen ist inzwischen zu gross, um ihn auf einmal zu lesen.",
  },
  "focus.figma.state.refresh.upstreamError": {
    en: "Loom tried to read it again, but Figma had a problem answering.",
    de: "Loom wollte es noch einmal lesen, aber Figma hatte ein Problem beim Antworten.",
  },
  "focus.figma.state.refresh.degraded": {
    en: "Loom tried to read it again, and only part of it came through.",
    de: "Loom wollte es noch einmal lesen, aber nur ein Teil kam an.",
  },
  "focus.figma.state.refresh.other": {
    en: "Loom tried to read it again, and that didn't work.",
    de: "Loom wollte es noch einmal lesen, das hat nicht geklappt.",
  },

  // --- The Create wizard's task step ---
  // One quiet line under the box, only when the text links a design Loom could
  // read AND Figma is not connected. The issue is filed before any chip can
  // exist, so this is the one place to say it before the user is mid-question.
  "focus.figma.create.linkNeedsConnect": {
    en: "Your text links a Figma design. Connect Figma so Loom can read it while you refine this issue.",
    de: "Dein Text verlinkt ein Figma-Design. Verbinde Figma, damit Loom es lesen kann, während du dieses Issue verfeinerst.",
  },

  // --- Disconnect asks first (components/FigmaConnection.tsx). The buttons
  // reuse "Disconnect" above and the common Cancel / Close. ---
  "focus.figma.connection.confirm.title": {
    en: "Disconnect Figma?",
    de: "Figma trennen?",
  },
  "focus.figma.connection.confirm.body": {
    en: "Loom will stop reading designs for you. Figma links in your open chats will show as not connected.",
    de: "Loom liest dann keine Designs mehr für dich. Figma-Links in deinen offenen Chats zeigen dann, dass Figma nicht verbunden ist.",
  },

  // --- The chip panel (focus/figma/FigmaLinkPanel.tsx). It sits under a chat
  // message AND inside a refine step, so it names neither: "this chat" was
  // wrong on refine. ---
  "focus.figma.panel.regionLabel": {
    en: "Figma links you sent",
    de: "Figma-Links, die du geschickt hast",
  },
  // The second ask, and the last one. Offered only once a first "Later" exists.
  // It lasts as long as this conversation with Loom, so "for now", not "ever".
  "focus.figma.panel.neverAsk": {
    en: "Don't ask again for now",
    de: "Vorerst nicht mehr fragen",
  },

  // --- The sketch (focus/figma/FigmaPreview.tsx) ---
  //
  // ONE component, two hosts, and the copy carries the difference. In a flow
  // step `question` is the headline and the answer is a user act with a yes/no
  // meaning; in chat `chatTitle` is the header and there is nothing to answer.
  //
  // The headline is ALWAYS one of these keys and never the ask's own
  // `question` field, which is the boundary that keeps a sentence inside
  // someone's design from becoming the question Loom is asking.
  "focus.figma.preview.question": {
    en: "Is this the screen you meant?",
    de: "Ist das die Ansicht, die du meinst?",
  },
  "focus.figma.preview.kicker": {
    en: "The design you linked",
    de: "Das verlinkte Design",
  },
  // Sits ON the border of the drawing, always visible. Together with the
  // border and the different letterform it is what makes "this is a picture of
  // someone else's screen" readable at a glance rather than inferred.
  "focus.figma.preview.fromFigma": { en: "From Figma", de: "Aus Figma" },
  // The fixed half of the claim. The counted half (how many shapes came out as
  // plain boxes, and so on) lives in `plurals.ts` and renders only above zero.
  "focus.figma.preview.sketchNote": {
    en: "A sketch, not a screenshot — it shows where things sit, not what they are.",
    de: "Eine Skizze, kein Bildschirmfoto – sie zeigt, wo etwas liegt, nicht was es ist.",
  },
  "focus.figma.preview.yes": { en: "Yes, that's it", de: "Ja, das ist sie" },
  "focus.figma.preview.no": { en: "No, wrong screen", de: "Nein, falsche Ansicht" },
  "focus.figma.preview.approved": {
    en: "Thanks — Loom works from this screen.",
    de: "Danke – Loom arbeitet mit dieser Ansicht.",
  },
  "focus.figma.preview.rejected": {
    en: "Got it. Paste the link to the screen you mean and Loom will read that one.",
    de: "Verstanden. Füge den Link zu der Ansicht ein, die du meinst, dann liest Loom diese.",
  },
  // Said because telling someone to paste another link without naming what it
  // costs would be lying about cost: a read is one of roughly twenty a month.
  "focus.figma.preview.rejectedCost": {
    en: "Reading another screen uses one of the few Figma reads this account has left.",
    de: "Eine weitere Ansicht zu lesen verbraucht einen der wenigen Figma-Abrufe, die dieses Konto noch hat.",
  },
  "focus.figma.preview.chatTitle": {
    en: "What Loom read",
    de: "Was Loom gelesen hat",
  },
  // `{frame}` comes from the CHIP's own state, never from the drawing's
  // payload — which is what lets the payload stay free of layer names while
  // the caption still says which screen this is.
  "focus.figma.preview.chatSubtitle": {
    en: "{frame} — a sketch",
    de: "{frame} – eine Skizze",
  },
  // The same line with NO name to put in it.
  //
  // Not a nicety: the three design names are live-only
  // (`redactEventForPersistence` strips `FIGMA_LINK_NAME_FIELDS` on the way into
  // both durable logs), so a chip that came back from a replay has no
  // `frameName` and the named line above cannot be rendered. Without this one
  // the panel said "What Loom read" and then nothing — silent about what it was
  // showing in the one situation where the reader is least sure.
  //
  // INDEFINITE, and that is the load-bearing word. Two different chips land
  // here. One is the replayed frame chip above, where "the screen" is true. The
  // other is a WHOLE-FILE chip: it addresses the latest frame read inside the
  // file, and `read-state.ts`'s rule 2 deliberately withholds a frame name from
  // it (the chip is about the file, and naming it after one frame would claim
  // something the chip never claims) — so it arrives here too, possibly with
  // three frame reads behind it. "The screen" asserts a singularity that chip
  // does not have. "A screen" is true on both, which is the only thing this
  // line can be: the widget cannot tell the two apart, and it must not, because
  // the name it would need is the one thing the payload may never carry.
  "focus.figma.preview.chatSubtitleUnnamed": {
    en: "A sketch of a screen Loom read",
    de: "Eine Skizze einer Ansicht, die Loom gelesen hat",
  },
  // WHEN the bytes under the picture were read from Figma. The drawing is
  // always the newest stored bytes for that frame, so without this an old
  // conversation can show today's design and say nothing about it. Sits beside
  // the size in the caption row, in the same relative wording the chip's own
  // meta line uses.
  //
  // It does NOT say "from Loom's copy", and the omission is the point.
  // `focus.figma.meta.fromStore` above owns that phrase for a different fact:
  // "this READ was answered out of the store and spent no Figma call" — the
  // `provenance = figma | store` distinction `figma.read` and the action ledger
  // are built on. This line means "the DRAWING is made of stored bytes", which
  // is true of every drawing there has ever been. Sharing the phrase made a
  // fresh Figma read render a chip with no store line above a sketch claiming
  // Loom's copy, and a store-served read say it twice, ~40px apart and in
  // German almost word for word. Naming FIGMA and the instant says the one
  // thing this line is for and collides with nothing.
  "focus.figma.preview.readAt": {
    en: "read from Figma {when}",
    de: "{when} von Figma gelesen",
  },
  "focus.figma.preview.show": { en: "Show the sketch", de: "Skizze anzeigen" },
  "focus.figma.preview.hide": { en: "Hide the sketch", de: "Skizze ausblenden" },
  "focus.figma.preview.bigger": { en: "Bigger", de: "Grösser" },
  "focus.figma.preview.closeBigger": { en: "Close", de: "Schliessen" },
  "focus.figma.preview.loading": { en: "Drawing it…", de: "Wird gezeichnet…" },
  // The raw bytes are past their horizon, or the link registry was rebuilt and
  // no longer knows this handle. Says what IS still there rather than what is
  // missing — the words Loom read never go away.
  "focus.figma.preview.noDrawing": {
    en: "Loom can't draw this one — only the words were kept for this design, not the layout.",
    de: "Loom kann diese nicht zeichnen – zu diesem Design sind nur die Wörter gespeichert, nicht das Layout.",
  },
  // The handle, not the design. `noDrawing` above says "only the words were
  // kept for this design, not the layout" — a sentence about the BYTES, and it
  // is false here: the bytes are very likely still on the row, and the frame
  // chip beside this one draws a picture off exactly that row. What is gone is
  // the in-process map from this chip to the read it was about, which a server
  // restart rebuilds from the durable turns — except for a whole-file chip,
  // whose read named a frame the event log deliberately does not carry. Says
  // what is true and what is still there, and offers nothing that would spend a
  // Figma read.
  "focus.figma.preview.lostHandle": {
    en: "After a restart Loom no longer knows which screen this link was read for, so it can't draw it. The words Loom read are still there.",
    de: "Nach einem Neustart weiss Loom nicht mehr, zu welcher Ansicht dieser Link gelesen wurde, und kann sie darum nicht zeichnen. Die Wörter, die Loom gelesen hat, sind trotzdem da.",
  },
  "focus.figma.preview.failed": {
    en: "The sketch didn't come out. The words Loom read are still there.",
    de: "Die Skizze hat nicht geklappt. Die Wörter, die Loom gelesen hat, sind trotzdem da.",
  },
  // The single control on a step whose drawing could not be built. A question
  // with no way past it is the one outcome this feature must never produce.
  "focus.figma.preview.continue": { en: "Carry on", de: "Weiter" },
  "focus.figma.preview.topOnly": {
    en: "Only the top part fits here.",
    de: "Hier passt nur der obere Teil.",
  },
  "focus.figma.preview.textList": {
    en: "The words on this screen",
    de: "Die Wörter auf dieser Ansicht",
  },
  "focus.figma.preview.size": { en: "{w} × {h}", de: "{w} × {h}" },
  // The accessible name of the drawing, assembled ENTIRELY from this sentence
  // and Loom's own counts. A label built from design strings would be chrome
  // imitation delivered straight into a screen reader, where the border, the
  // caption chip and the letterform all vanish.
  "focus.figma.preview.aria": {
    en: "A sketch of a screen {w} by {h}, with {texts} pieces of text and {shapes} shapes.",
    de: "Eine Skizze einer Ansicht, {w} mal {h}, mit {texts} Textstellen und {shapes} Formen.",
  },
};
